import React, { useState, useEffect, useRef, useMemo, useCallback } from "react";
import Header from "../component/Header";
import Sidebar from '../component/Sidebar';
import ChartCard from "../component/ChartCard";
import '../css/sidebar.css';
import '../css/setTargetModal.css';
import { NavLink } from 'react-router-dom';
import { Tooltip, Whisper } from "rsuite";
import "rsuite/dist/rsuite.min.css";
import { Placeholder } from 'rsuite';
import LogoApple from "../images/company-logos/apple.svg";
import LogoIgne from "../images/company-logos/igne.svg";
import LogoTesla from "../images/company-logos/tesla.svg";
import LogoJNJ from "../images/company-logos/jnj.svg";
import SettingIcon from "../images/iconSetting.svg";
import { toast } from "react-toastify";
import { SelectPicker, Message } from "rsuite";
import { motion, AnimatePresence } from "framer-motion";
import ArrowTrendUp from "../images/arrow-trend-up.svg";
import ArrowTrendDown from "../images/arrow-trend-down.svg";
import iconGraph from "../images/iconGraph.svg";
import iconFilledOrder from "../images/iconFilledOrder.svg";
import iconPendingOrder from "../images/iconPendingOrder.svg";
import iconTotalOrder from "../images/iconTotalOrder.svg";
import iconRejectedOrder from "../images/iconRejectedOrder.svg";
import { MdKeyboardDoubleArrowLeft } from "react-icons/md";
import { MdKeyboardDoubleArrowRight } from "react-icons/md";
import { IoIosArrowBack } from "react-icons/io";
import { IoIosArrowForward } from "react-icons/io";
import { useNavigate } from 'react-router-dom';
import MarketDepthIcon from "../component/MarketDepthIcon";
import MarketDepthPopover from "../component/MarketDepthPopover";
import TablePageSizeSelect from "../component/TablePageSizeSelect";
import { getCurrentLanguage, t, translateColumnLabel } from "../i18n";
import { EllipsisVertical, ArrowUp, ArrowDown } from "lucide-react";
import { createPortal } from "react-dom";
import fivePaisaWebSocketService from "../services/fivePaisaWebSocketService";
import growwLivePriceService from "../services/growwLivePriceService";
import angelOneWebSocketService, {
  getAngelExchangeTypeFromHolding,
} from "../services/angelOneWebSocketService";
import angelOneMarketDepthService, {
  normalizeAngelMarketDepth,
} from "../services/angelOneMarketDepthService";


import {
  placeOrderViaPms,
  modifyOrderViaPms,
  cancelOrderViaPms,
  normalizePlaceOrderResponse,
  normalizeAngelOneVariety,
  isFivePaisaBroker,
  runPlaceOrderAcceptedFlow,
} from "../services/placeOrderService";


const GROW_POLL_SOURCE = "orders";

const toPmsBrokerFlag = (brokerType) => {
  const normalized = String(brokerType || "").trim().toLowerCase();
  if (normalized === "grow" || normalized === "5paisa" || normalized === "angelone") {
    return normalized;
  }
  return "5paisa";
};

function Orders(props) {
  return <OrdersContent {...props} />;
}


const ORDER_PMS_COLUMN_KEY_TO_TABLE_COL = {
  name: "Name",
  p_q: "PQty",
  order_id: "Order Id",
  current_price: "Current Price",
  order_status: "Order Status",
  type: "Type",
  reason: "Reason",
  date: "Date",
  quantity: "Quantity",
  net_change: "Net Change",
  exch: "Exch",
};

const ORDER_TABLE_COL_LABEL_ALIASES = {
  "Order ID": "Order Id",
  "P/Q": "PQty",
};

const ORDER_TABLE_COL_TO_PMS_COLUMN_KEY = Object.fromEntries(
  Object.entries(ORDER_PMS_COLUMN_KEY_TO_TABLE_COL).map(([apiKey, tableCol]) => [
    tableCol,
    apiKey,
  ])
);

function mapOrderPmsColumnToTableCol(columnKey, columnLabel) {
  const key = String(columnKey || "").toLowerCase().trim();
  if (ORDER_PMS_COLUMN_KEY_TO_TABLE_COL[key]) {
    return ORDER_PMS_COLUMN_KEY_TO_TABLE_COL[key];
  }
  const label = String(columnLabel || "").trim();
  if (ORDER_TABLE_COL_LABEL_ALIASES[label]) {
    return ORDER_TABLE_COL_LABEL_ALIASES[label];
  }
  const known = new Set([
    "Name",
    "Current Price",
    "Net Change",
    "Order Id",
    "PQty",
    "Type",
    "Quantity",
    "Order Status",
    "Reason",
    "Date",
    "Exch",
    "Actions",
  ]);
  if (known.has(label)) return label;
  return null;
}

function buildOrdersTableColumnOrderFromPms(columnsPayload) {
  if (!Array.isArray(columnsPayload) || columnsPayload.length === 0) return null;
  const sorted = [...columnsPayload].sort(
    (a, b) => Number(a.sort_order) - Number(b.sort_order)
  );
  const out = [];
  const seen = new Set();
  for (const c of sorted) {
    const selected =
      c.is_selected === "1" || c.is_selected === 1 || c.is_selected === true;
    const active = c.is_active === "1" || c.is_active === 1 || c.is_active === true;
    if (!selected || !active) continue;
    const col = mapOrderPmsColumnToTableCol(c.column_key, c.column_label);
    if (!col || col === "Actions") continue;
    if (seen.has(col)) continue;
    seen.add(col);
    out.push(col);
  }
  if (!out.includes("Actions")) out.push("Actions");
  return out.length ? out : null;
}

function OrdersContent(props) {
  // All initialization and Declaration
  const sharedData = JSON.parse(localStorage.getItem("sharedData") || "null");
  const activeBrokerFlag = toPmsBrokerFlag(
    sharedData?.broker_type || localStorage.getItem("broker_type") || "5paisa",
  );
  const isAngelOneBroker = activeBrokerFlag === "angelone";

  const getOrderLivePriceCacheKey = (order) => {
    if (!order) return "";
    if (isAngelOneBroker) {
      return String(
        order?.token || order?.Token || order?.ScripCode || order?.scripcode || "",
      ).trim();
    }
    return Number(order?.ScripCode) || Number(order?.scripcode) || "";
  };

  const pmsApiBaseUrl =
    process.env.REACT_APP_PMS_API_BASE_URL || "https://pmsapi.infomanav.in";
  const [isLoading, setIsLoading] = useState(true);
  const [progress, setProgress] = useState(0);
  const [tabName, setTabName] = useState('Orders');
  const [orderBook, setOrderBook] = useState([]);
  const searchInputRef = useRef(null);
  // WebSocket related state variables
  const [, setConnected] = useState(false);
  const [liveUpdateTrigger, setLiveUpdateTrigger] = useState(0);
  const [isHovering, setIsHovering] = useState(false);
  const [hoveredTab, setHoveredTab] = useState(null);

  // Market depth state (stores the latest order book for subscribed stock)
  const [marketDepth, setMarketDepth] = useState([]);
  const [currentMDModal, setcurrentMDModal] = useState(null);
  const [marketDepthCache, setMarketDepthCache] = useState({});

  // Inside your component
  const navigate = useNavigate();
  const [currentLanguage, setCurrentLanguage] = useState(getCurrentLanguage());
  const tr = (key) => t(key, currentLanguage);


  // Refs for caching live data and WebSocket listeners
  const wsStatusUnsubRef = useRef(null);
  const wsPriceUnsubRef = useRef(null);
  const wsDepthUnsubRef = useRef(null);
  const wsErrorUnsubRef = useRef(null);
  const angelWsPriceUnsubRef = useRef(null);
  const angelWsErrorUnsubRef = useRef(null);
  const angelOrderSubscriptionsRef = useRef([]);
  const angelMdServiceUnsubRef = useRef(null);

  const livePriceCache = useRef({});
  const livePCloseCache = useRef({});
  const liveDiffCache = useRef({});
  const liveChangePercentageCache = useRef({});
  const liveVolumeCache = useRef({});
  const livePLCache = useRef({});
  const currentMDModalRef = useRef(null);
  const ordersFetchIdRef = useRef(0);
  const lastOrdersFetchRunRef = useRef({ key: "", at: 0 });

  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);


  const [showMarketDepth, setShowMarketDepth] = useState(false);
  const [marketDepthPosition, setMarketDepthPosition] = useState({ top: 0, left: 0 });
  const marketDepthButtonRef = useRef(null);
  const marketDepthPopoverRef = useRef(null);

  // Include click-outside useEffect





  const [columns, setColumns] = useState([
    "Name",
    "Current Price",
    "Net Change",
    "Order Id",
    "PQty",
    "Type",
    "Quantity",
    "Order Status",
    "Reason",
    "Date",
    // "Exch",
    "Actions"
  ]);




  // const [columns, setColumns] = useState([
  //   { key: "Name", label: "SName" }, 
  //   { key: "CurrentPrice", label: "Current Price" }, 
  //   { key: "NetChange", label: "Net Change" }, 
  //   { key: "OrderId", label: "Order Id" },
  //     { key: "PQty", label: "PQty" },
  //     { key: "Type", label: "Type" },
  //     { key: "Qty", label: "Quantity" },
  //     { key: "OrderStatus", label: "Order Status" },
  //     { key: "Reason", label: "Reason" },
  //     { key: "Date", label: "Date" },
  //     { key: "Exch", label: "Exch" },
  //     { key: "Actions", label: "" },
  //   ]);

  const OrderType = [
    { value: "Market Order", label: tr("marketOrder") },
    { value: "Limit Order", label: tr("limitOrder") },
  ];

  const [orderType, setOrderType] = useState("Market Order");
  useEffect(() => {
    const handleLanguageChange = (event) => {
      setCurrentLanguage(event?.detail?.language || getCurrentLanguage());
    };
    window.addEventListener("app-language-change", handleLanguageChange);
    return () => window.removeEventListener("app-language-change", handleLanguageChange);
  }, []);
  // const [qty, setQty] = useState(1);
  const [isPreview, setIsPreview] = useState(false);

  const [buySellModalDetails, setBuySellModalDetails] = useState({
    FullName: '',
    Symbol: '',
    Type: 'Buy', // hardcoded for buy function, will create separate for sell
    OrderQty: 1, // default 1
    LimitPrice: 0, // default 0
    order: null // complete order object for future use
  });
  const [modifyOrderModalDetails, setModifyOrderModalDetails] = useState({
    Symbol: "",
    FullName: "",
    quantity: 0,
    price: 0,
    triggerPrice: 0,
    orderType: "LIMIT",
    segment: "CASH",
    exchOrderID: "",
    growwOrderId: "",
    angelOrderId: "",
    variety: "NORMAL",
    producttype: "DELIVERY",
    duration: "DAY",
    order: null,
  });

  const modifyOrderTypeOptions = [
    { label: "MARKET", value: "MARKET" },
    { label: "LIMIT", value: "LIMIT" },
  ];
  const [isModifySubmitting, setIsModifySubmitting] = useState(false);
  const [cancelOrderModalDetails, setCancelOrderModalDetails] = useState({
    show: false,
    order: null,
    symbol: "",
  });
  const [isCancelSubmitting, setIsCancelSubmitting] = useState(false);


  // Drag-N-Drop
  // Add these in your component
  const [draggedColumn, setDraggedColumn] = useState(null);
  const [columnOrder, setColumnOrder] = useState(columns); // start with columns prop or state
  const [activeHeaderMenu, setActiveHeaderMenu] = useState(null);
  const [sortConfig, setSortConfig] = useState(null);



  // table search
  const [searchQuery, setSearchQuery] = useState("");
  const [triggerUpdate, setTriggerUpdate] = useState(0);



  //----------------------------// useEffects //----------------------------//

  useEffect(() => {
    document.title = tabName;
  }, [tabName]);



  useEffect(() => {
    const userId = Number(localStorage.getItem("id")) || Number(sharedData?.ID) || 0;
    if (!userId || !activeBrokerFlag) {
      setIsLoading(false);
      setOrderBook([]);
      return;
    }
    // React StrictMode in dev runs effects twice on mount — skip duplicate within 1.2s
    const runKey = `${userId}:${activeBrokerFlag}:${triggerUpdate}`;
    const now = Date.now();
    if (
      lastOrdersFetchRunRef.current.key === runKey &&
      now - lastOrdersFetchRunRef.current.at < 1200
    ) {
      return;
    }
    lastOrdersFetchRunRef.current = { key: runKey, at: now };
    orderBookList();
  }, [triggerUpdate, activeBrokerFlag]);

  useEffect(() => {
    if (activeBrokerFlag !== "5paisa") {
      return undefined;
    }
    wsStatusUnsubRef.current = fivePaisaWebSocketService.onStatus((status) => {
      setConnected(status?.type === "connected");
    });

    wsPriceUnsubRef.current = fivePaisaWebSocketService.onPrice((tick) => {
      if (tick) handleLivePriceUpdate(tick);
    });

    wsDepthUnsubRef.current = fivePaisaWebSocketService.onDepth((depthData) => {
      if (!depthData?.Token) return;
      setMarketDepthCache((prev) => ({
        ...prev,
        [depthData.Token]: depthData.Details || [],
      }));
      handleMarketDepthUpdate(depthData);
    });

    wsErrorUnsubRef.current = fivePaisaWebSocketService.onError((error) => {
      console.error("❌ WS service error:", error);
    });

    fivePaisaWebSocketService.connect();

    return () => {
      if (wsStatusUnsubRef.current) wsStatusUnsubRef.current();
      if (wsPriceUnsubRef.current) wsPriceUnsubRef.current();
      if (wsDepthUnsubRef.current) wsDepthUnsubRef.current();
      if (wsErrorUnsubRef.current) wsErrorUnsubRef.current();
      fivePaisaWebSocketService.disconnect({ manual: false });
    };
  }, [activeBrokerFlag]);

  useEffect(() => {
    if (activeBrokerFlag !== "angelone") {
      if (angelOrderSubscriptionsRef.current.length) {
        angelOneWebSocketService.unsubscribeMarketFeed(
          angelOrderSubscriptionsRef.current,
        );
        angelOrderSubscriptionsRef.current = [];
      }
      return undefined;
    }

    angelWsPriceUnsubRef.current = angelOneWebSocketService.onPrice((tick) => {
      if (tick) handleLivePriceUpdate(tick);
    });
    angelWsErrorUnsubRef.current = angelOneWebSocketService.onError((error) => {
      console.error("❌ AngelOne WS service error:", error);
    });

    angelOneWebSocketService.connect();

    return () => {
      angelWsPriceUnsubRef.current?.();
      angelWsErrorUnsubRef.current?.();
      angelWsPriceUnsubRef.current = null;
      angelWsErrorUnsubRef.current = null;

      if (angelOrderSubscriptionsRef.current.length) {
        angelOneWebSocketService.unsubscribeMarketFeed(
          angelOrderSubscriptionsRef.current,
        );
        angelOrderSubscriptionsRef.current = [];
      }
    };
  }, [activeBrokerFlag]);

  useEffect(() => {
    if (activeBrokerFlag !== "angelone") {
      angelOneMarketDepthService.closeDepth();
      return undefined;
    }

    angelMdServiceUnsubRef.current = angelOneMarketDepthService.subscribe(
      (depth) => {
        setMarketDepth(Array.isArray(depth) ? depth : []);
      },
    );

    return () => {
      angelMdServiceUnsubRef.current?.();
      angelMdServiceUnsubRef.current = null;
      angelOneMarketDepthService.closeDepth();
    };
  }, [activeBrokerFlag]);

  useEffect(() => {
    if (activeBrokerFlag !== "grow") {
      growwLivePriceService.stopPolling(GROW_POLL_SOURCE);
      return undefined;
    }

    const symbols = orderBook
      .map((order) => {
        const symbol = String(order?.ScripName || order?.Symbol || "").trim().toUpperCase();
        if (!symbol) return "";
        const exchangePrefix = order?.Exch === "B" ? "BSE" : "NSE";
        return `${exchangePrefix}_${symbol}`;
      })
      .filter(Boolean);

    growwLivePriceService.setSymbols(GROW_POLL_SOURCE, symbols);
    const unsubscribe = growwLivePriceService.subscribe((payload) => {
      let updated = false;
      orderBook.forEach((order) => {
        const symbol = String(order?.ScripName || order?.Symbol || "").trim().toUpperCase();
        if (!symbol) return;
        const exchangePrefix = order?.Exch === "B" ? "BSE" : "NSE";
        const key = `${exchangePrefix}_${symbol}`;
        const ltp = Number(payload?.[key]);
        if (Number.isFinite(ltp)) {
          livePriceCache.current[order.ScripCode] = ltp;
          updated = true;
        }
      });
      if (updated) setLiveUpdateTrigger((prev) => prev + 1);
    });

    growwLivePriceService.startPolling(1000);

    return () => {
      unsubscribe?.();
      growwLivePriceService.stopPolling(GROW_POLL_SOURCE);
    };
  }, [activeBrokerFlag, orderBook]);





  // MarketDepth
  useEffect(() => {
    const modalEl = document.getElementById("MarketDepthModal");
    if (modalEl) {
      // Attach event listener
      modalEl.addEventListener("hidden.bs.modal", handleModalClose);
    }
    // Cleanup when component unmounts
    return () => {
      if (modalEl) {
        modalEl.removeEventListener("hidden.bs.modal", handleModalClose);
      }
    };
  }, []);

  // Keep ref updated
  useEffect(() => {
    currentMDModalRef.current = currentMDModal;
  }, [currentMDModal]);



  useEffect(() => {
    console.log("📉New Modal Data: ", currentMDModal)
  }, [currentMDModal])




  // Click-outside handler
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        marketDepthPopoverRef.current &&
        !marketDepthPopoverRef.current.contains(event.target) &&
        marketDepthButtonRef.current &&
        !marketDepthButtonRef.current.contains(event.target)
      ) {
        closeMarketDepthPopover();
      }
    };

    if (showMarketDepth) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showMarketDepth]);




  //----------------------------// marketdepth Functions //----------------------------//


  const handleMarketDepthClickOrder = (order, e) => {
    const button = e.currentTarget;
    const buttonRect = button.getBoundingClientRect();

    const popoverWidth = 480;
    const popoverHeight = 500;
    const padding = 16;

    let left = buttonRect.left - popoverWidth - 12;
    let top = buttonRect.top - 20;

    if (left < padding) {
      left = buttonRect.right + 12;
    }

    if (top < padding) {
      top = buttonRect.top + buttonRect.height + 12;
    }

    if (top + popoverHeight > window.innerHeight - padding) {
      top = window.innerHeight - popoverHeight - padding;
    }

    if (left + popoverWidth > window.innerWidth - padding) {
      left = window.innerWidth - popoverWidth - padding;
    }

    setMarketDepthPosition({
      top: Math.max(padding, top),
      left: Math.max(padding, left),
    });
    setShowMarketDepth(true);
    setcurrentMDModal(order);
    if (isAngelOneBroker) {
      const cachedDepth = Array.isArray(order?.marketDepth)
        ? order.marketDepth
        : normalizeAngelMarketDepth(order?.market_quote?.depth);
      if (cachedDepth.length) {
        setMarketDepth(cachedDepth);
      }
    }
    subscribeToMarketDepth(order);
  };








  //----------------------------// Functions //----------------------------//



  // Notification sound function
  const pingNotification = () => {
    try {
      const audio = new Audio('/sounds/notifications.mp3');
      audio.play().catch(error => {
        console.error('Failed to play notification sound:', error);
      });
    } catch (error) {
      console.error('Error creating audio:', error);
    }
  };


  // Common toast options
  const toastOptions = {
    position: "top-center",
    autoClose: 10000,
    hideProgressBar: false,
    closeOnClick: true,
    pauseOnHover: true,
    draggable: true,
    theme: "light",
  };


  // Success toast function
  const showSuccessToast = (message) => {
    toast.success(message, {
      ...toastOptions,
      icon: "✅",
    });
    pingNotification()
  };

  // Error toast function
  const showErrorToast = (message) => {
    toast.error(message, {
      ...toastOptions,
      icon: "⚠️",
    });
    pingNotification()
  };

  // Error toast function
  const showWarningToast = (message) => {
    toast.warn(message, {
      ...toastOptions,
      icon: "⚠️",
    });
    pingNotification()
  };





  //----------------------------// table Search Functions //----------------------------//


  // Add this before your tbody
  const filteredOrderBook = orderBook.filter((order) => {
    if (!searchQuery) return true;

    const searchLower = searchQuery.toLowerCase();
    const scripName = (order.ScripName || "").toLowerCase();

    return scripName.includes(searchLower);
  });

  const getOrderSortableValue = useCallback((order, columnKey) => {
    if (!order) return "";
    const raw =
      order?.[columnKey] ??
      order?.[String(columnKey || "").replace(/\s+/g, "")] ??
      "";
    const num = Number(String(raw).replace(/,/g, ""));
    return Number.isFinite(num) && raw !== "" ? num : String(raw).toLowerCase();
  }, []);

  const sortedOrderBook = useMemo(() => {
    if (!sortConfig?.column || !sortConfig?.direction) return filteredOrderBook;
    const dir = sortConfig.direction === "asc" ? 1 : -1;
    return [...filteredOrderBook].sort((a, b) => {
      const aVal = getOrderSortableValue(a, sortConfig.column);
      const bVal = getOrderSortableValue(b, sortConfig.column);
      if (typeof aVal === "number" && typeof bVal === "number") return (aVal - bVal) * dir;
      return String(aVal).localeCompare(String(bVal), undefined, { numeric: true, sensitivity: "base" }) * dir;
    });
  }, [filteredOrderBook, getOrderSortableValue, sortConfig]);


  //----------------------------// End table Search Functions //----------------------------//

  class BaseOrderAdapter {
    numberOrZero = (value) => {
      const num = Number(value);
      return Number.isFinite(num) ? num : 0;
    };

    stringOrDefault = (value, fallback = "") =>
      value === null || value === undefined || value === "" ? fallback : String(value);

    normalizeOrders = (orders = []) => orders;
  }

  class FivePaisaOrderAdapter extends BaseOrderAdapter {
    mapStatus = (status) => {
      const normalizedStatus = String(status || "").toUpperCase().trim();
      if (normalizedStatus === "ACKED") return "Scheduled (AMO)";
      return this.stringOrDefault(status, "Pending");
    };

    normalizeOrders = (orders = []) =>
      orders.map((order = {}) => ({
        ...order,
        Exch: this.stringOrDefault(order?.Exch, "N").charAt(0).toUpperCase(),
        ExchType: this.stringOrDefault(order?.ExchType, "C").charAt(0).toUpperCase(),
        ScripCode: this.numberOrZero(order?.ScripCode),
        ScripName: this.stringOrDefault(order?.ScripName, "N/A"),
        FullName: this.stringOrDefault(order?.FullName || order?.ScripName, "N/A"),
        CurrentPrice: this.numberOrZero(order?.AveragePrice ?? order?.Rate),
        PreviousClose: this.numberOrZero(order?.Rate ?? order?.AveragePrice),
        BrokerOrderId: this.stringOrDefault(order?.BrokerOrderId, ""),
        Qty: this.numberOrZero(order?.Qty),
        PendingQty: this.numberOrZero(order?.PendingQty),
        TradedQty: this.numberOrZero(order?.TradedQty),
        BuySell: this.stringOrDefault(order?.BuySell, "B"),
        OrderStatus: this.mapStatus(order?.OrderStatus),
        Reason: this.stringOrDefault(order?.Reason, ""),
      }));
  }

  class GrowOrderAdapter extends BaseOrderAdapter {
    mapStatus = (status) => {
      const s = String(status || "").toUpperCase();
      if (s.includes("EXECUTED") || s === "COMPLETE" || s === "COMPLETED") {
        return "Fully Executed";
      }
      if (s.includes("PARTIAL")) return "Partially Completed";
      if (s.includes("PENDING") || s.includes("OPEN")) return "Pending";
      if (s.includes("CANCEL")) return "Cancelled";
      if (s === "ACKED") return "Scheduled (AMO)";
      if (s.includes("REJECT") || s === "FAILED" || s.includes("FAIL")) {
        return "Rejected";
      }
      return this.stringOrDefault(status, "Pending");
    };

    normalizeOrders = (orders = []) =>
      orders.map((order = {}) => {
        const stocksData = order?.stocks_data || {};
        const ohlc = stocksData?.ohlc || {};
        const symbol = this.stringOrDefault(order?.trading_symbol, "N/A");
        const exch = String(order?.exchange || "NSE").toUpperCase().startsWith("B")
          ? "B"
          : "N";
        const qty = this.numberOrZero(order?.quantity);
        let pendingQty = this.numberOrZero(order?.remaining_quantity);
        let tradedQty = this.numberOrZero(order?.filled_quantity);
        const rawStatus = String(order?.order_status || "").toUpperCase();
        const rawAmoStatus = String(order?.amo_status || "").toUpperCase();
        const isScheduledAmo =
          rawStatus === "ACKED" ||
          rawAmoStatus === "PARKED" ||
          rawAmoStatus === "SCHEDULED";
        if (
          pendingQty === 0 &&
          tradedQty === 0 &&
          qty > 0 &&
          (rawStatus.includes("PENDING") || rawStatus.includes("OPEN") || isScheduledAmo)
        ) {
          pendingQty = qty;
        }
        if (
          tradedQty === 0 &&
          qty > 0 &&
          (rawStatus.includes("EXECUTED") ||
            rawStatus.includes("COMPLETE") ||
            rawStatus === "FILLED")
        ) {
          tradedQty = qty;
        }
        const avgPrice = this.numberOrZero(order?.average_fill_price ?? order?.price);
        const marketLtp = this.numberOrZero(
          stocksData?.close ??
          stocksData?.last_price ??
          stocksData?.LastRate ??
          stocksData?.ltp ??
          ohlc?.close ??
          stocksData?.high ??
          stocksData?.open ??
          ohlc?.open ??
          order?.ltp ??
          order?.last_price ??
          order?.current_price ??
          avgPrice,
        );
        const previousClose = this.numberOrZero(
          order?.PreviousClose ??
          order?.previous_close ??
          stocksData?.PClose ??
          stocksData?.previous_close ??
          stocksData?.pc ??
          stocksData?.open ??
          ohlc?.open ??
          avgPrice,
        );
        return {
          ...order,
          Exch: exch,
          ExchType: String(order?.segment || "CASH").toUpperCase().includes("CASH") ? "C" : "D",
          ScripCode: this.stringOrDefault(order?.isin || symbol, symbol),
          ScripName: symbol,
          FullName: this.stringOrDefault(order?.name, symbol),
          name: this.stringOrDefault(order?.name, symbol),
          CurrentPrice: marketLtp,
          PreviousClose: previousClose,
          BrokerOrderId: this.stringOrDefault(order?.groww_order_id, ""),
          BrokerOrderTime: this.stringOrDefault(order?.exchange_time || order?.created_at, ""),
          Qty: qty,
          PendingQty: pendingQty,
          TradedQty: tradedQty,
          BuySell:
            String(order?.transaction_type || "").toUpperCase() === "SELL" ? "S" : "B",
          OrderStatus: this.mapStatus(order?.order_status),
          Reason: this.stringOrDefault(order?.remark ?? order?.Remark, ""),
        };
      });
  }

  class AngelOneOrderAdapter extends BaseOrderAdapter {
    mapStatus = (status) => {
      const s = String(status || "").toLowerCase().trim();
      if (s.includes("reject") || s.includes("failed")) return "Rejected";
      if (s.includes("cancel")) return "Cancelled";
      if (
        s.includes("complete") ||
        s.includes("executed") ||
        s === "traded" ||
        s === "filled" ||
        s === "success"
      ) {
        return "Fully Executed";
      }
      if (s.includes("partial")) return "Partially Completed";
      if (
        s.includes("open") ||
        s.includes("pending") ||
        s.includes("validation") ||
        s.includes("trigger") ||
        s === "after market order req received"
      ) {
        return "Pending";
      }
      return this.stringOrDefault(status, "Pending");
    };

    parseTradingSymbol = (tradingsymbol) => {
      const raw = this.stringOrDefault(tradingsymbol, "N/A");
      const base = raw.includes("-") ? raw.split("-")[0] : raw;
      return { raw, base: this.stringOrDefault(base, raw) };
    };

    normalizeOrders = (orders = []) =>
      orders.map((order = {}) => {
        const { raw: tradingSymbol, base: symbolBase } = this.parseTradingSymbol(
          order?.tradingsymbol,
        );
        const exchRaw = String(order?.exchange || "NSE").toUpperCase();
        const exch = exchRaw.startsWith("B")
          ? "B"
          : exchRaw.startsWith("M")
            ? "M"
            : "N";
        const productType = String(order?.producttype || "DELIVERY").toUpperCase();
        const exchType =
          productType.includes("DELIVERY") || productType === "CNC" ? "C" : "D";
        const qty = this.numberOrZero(order?.quantity);
        const pendingQty = this.numberOrZero(order?.unfilledshares);
        const tradedQty = this.numberOrZero(order?.filledshares);
        const price = this.numberOrZero(order?.price);
        const avgPrice = this.numberOrZero(order?.averageprice);
        const orderType = this.stringOrDefault(order?.ordertype, "MARKET").toUpperCase();
        const isMarket = orderType === "MARKET";
        const token = this.numberOrZero(order?.symboltoken);
        const statusRaw = this.stringOrDefault(
          order?.orderstatus || order?.status,
          "",
        );

        return {
          ...order,
          Exch: exch,
          ExchType: exchType,
          ScripCode: token,
          Symbol: symbolBase,
          TradingSymbol: tradingSymbol,
          tradingsymbol: tradingSymbol,
          ScripName: tradingSymbol,
          FullName: symbolBase,
          name: symbolBase,
          symboltoken: String(order?.symboltoken || token || ""),
          CurrentPrice: avgPrice > 0 ? avgPrice : price,
          PreviousClose: price,
          AveragePrice: avgPrice > 0 ? avgPrice : price,
          Rate: price,
          Price: price,
          AtMarket: isMarket ? "Y" : "N",
          OrderType: orderType,
          order_type: orderType,
          BrokerOrderId: this.stringOrDefault(order?.orderid, ""),
          orderid: this.stringOrDefault(order?.orderid, ""),
          producttype: productType,
          variety: normalizeAngelOneVariety(order?.variety),
          duration: this.stringOrDefault(order?.duration, "DAY"),
          ExchOrderID: this.stringOrDefault(order?.exchangeorderid, ""),
          exchange_order_id: this.stringOrDefault(order?.exchangeorderid, ""),
          BrokerOrderTime: this.stringOrDefault(
            order?.updatetime ||
              order?.exchorderupdatetime ||
              order?.exchtime ||
              order?.filltime,
            "",
          ),
          Qty: qty,
          PendingQty: pendingQty,
          TradedQty: tradedQty,
          BuySell:
            String(order?.transactiontype || "").toUpperCase() === "SELL" ? "S" : "B",
          OrderStatus: this.mapStatus(statusRaw),
          Reason: this.stringOrDefault(order?.text || order?.Reason, ""),
          token: String(order?.symboltoken || token || ""),
          segment: productType.includes("DELIVERY") ? "CASH" : productType,
        };
      });
  }

  const getOrderAdapter = (brokerFlag) => {
    const normalizedFlag = String(brokerFlag || "").toLowerCase();
    if (normalizedFlag === "grow") return new GrowOrderAdapter();
    if (normalizedFlag === "5paisa") return new FivePaisaOrderAdapter();
    if (normalizedFlag === "angelone") return new AngelOneOrderAdapter();
    return new BaseOrderAdapter();
  };






  //  const orderBookList = async () => {

  //   let columnsToUse = columns || []; // fallback to existing columns state

  //   // Fetch column order from API first
  //   try {
  //     const colOrderResponse = await fetch("https://beta.infomanav.in/keep/zerodhaAPI/5paisa_columnOrder_2/get_col_order_and_visibility.php", {
  //       method: "POST",
  //       headers: { "Content-Type": "application/json" },
  //       body: JSON.stringify({ module_name: "Orders", user_id: sharedData.ID }),
  //     });

  //     const colOrderResult = await colOrderResponse.json();

  //     if (colOrderResult.status === "success" && Array.isArray(colOrderResult.columnOrder)) {
  //       // Add "Actions" column if missing
  //       setColumnOrder(colOrderResult.columnOrder);
  //       if (!colOrderResult.columnOrder.includes("Actions")) {
  //         colOrderResult.columnOrder.push("Actions");
  //       }
  //       setColumns(colOrderResult.columnOrder);
  //       columnsToUse = colOrderResult.columnOrder; // use updated columns immediately
  //     } else {
  //       console.warn("Failed to fetch column order, using default");
  //     }
  //   } catch (error) {
  //     console.error("Error fetching column order:", error);
  //   }

  //   console.log("✅📉Columns is: ", columnsToUse);




  //     setProgress(10);
  //     // Order Book/List
  //     fetch("https://Openapi.5paisa.com/VendorsAPI/Service1.svc/V3/OrderBook", {
  //       method: "POST",
  //       headers: {
  //         Authorization: `bearer ${sharedData.accessToken}`,
  //         "Content-Type": "application/json",
  //         Cookie:
  //           "NSC_JOh0em50e1pajl5b5jvyafempnkehc3=ffffffffaf103e0f45525d5f4f58455e445a4a423660; 5paisacookie=gbs1pap34z1awfx2fq41xxuh; NSC_JOh0em50e1pajl5b5jvyafempnkehc3=ffffffffaf103e0c45525d5f4f58455e445a4a423660",
  //       },
  //       body: JSON.stringify({
  //         head: {
  //           key: sharedData.api_key,
  //         },
  //         body: {
  //           ClientCode: sharedData.ClientCode,
  //         },
  //       }),
  //     })
  //       .then((response) => {
  //         if (!response.ok) {
  //           throw new Error(
  //             `Network response was not ok. Status: ${response.status}`
  //           );
  //         }
  //         return response.json();
  //       })
  //       .then((data) => {
  //         // Handle the response data here
  //         console.log("ORDER BOOK DATA:", data.body.OrderBookDetail);
  //         setOrderBook(data.body.OrderBookDetail.reverse());


  //         let holdings = data.body.OrderBookDetail || [];


  //          // ✨ Add ScripCode to each holding based on Exch value
  //           holdings = holdings.map(holding => ({
  //             ...holding,
  //             ScripCode: holding.ScripCode ? Number(holding.ScripCode) : 0
  //           }));


  //             // 🔥 Connect to WebSocket after fetching holdings
  //           handleConnectClick();



  //             if (data.body.Message === "Success" && data.body.OrderBookDetail.length > 0) {
  //                   // Update subscribed stocks state for WebSocket
  //                   setSubscribedStocks((prev) => {
  //                     const newStocks = holdings.map((item) => ({
  //                       Scripcode: item.scripcode,
  //                       Exch: item.exch?.charAt(0),
  //                       ExchType: item.exchType?.charAt(0), 
  //                       Symbol: item.ScripName,
  //                     }));

  //                     const merged = [...prev, ...newStocks];
  //                     const unique = merged.filter(
  //                       (v, i, a) => a.findIndex((t) => t.Scripcode === v.Scripcode) === i
  //                     );

  //                     return unique;
  //                   });


  //                 // 🔥 Subscribe each holding to WebSocket - Wait for connection
  //             const subscribeWhenReady = () => {
  //               // Store holdings for resubscription after reconnects
  //               lastSubscribedHoldings.current = holdings;

  //               const checkConnection = () => {
  //                 if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
  //                   console.log("🚀 WebSocket ready, subscribing to holdings...");
  //                   holdings.forEach((holding) => {
  //                     sendMessageToWebSocket(holding, sharedData.ClientCode, "Portfolio");
  //                   });
  //                 } else {
  //                   console.log("⏳ Waiting for WebSocket connection...");
  //                   setTimeout(checkConnection, 500);
  //                 }
  //               };

  //               // Start checking after 1 second
  //               setTimeout(checkConnection, 1000);
  //             };

  //             subscribeWhenReady();



  //                 }




  //         // setIsLoading(false);
  //         setTimeout(() => {
  //           setIsLoading(false);
  //         }, 1500);
  //         setProgress(100);



  //       })
  //       .catch((error) => {
  //         // Handle any errors here
  //         console.error("There was a problem with the fetch operation:", error);
  //         setIsLoading(false);
  //       });
  //   };

  const orderBookList = async () => {
    const fetchId = ++ordersFetchIdRef.current;
    let columnsToUse = columns || [];

    const brokerFlag = toPmsBrokerFlag(activeBrokerFlag);
    const userEmail =
      sharedData?.Email_ID ||
      sharedData?.email ||
      localStorage.getItem("email") ||
      "";

    // Fetch column order from PMS API (replaces legacy beta columnOrder endpoint)
    try {
      if (userEmail) {
        const colOrderResponse = await fetch(`${pmsApiBaseUrl}/api/v1/columns/list`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            broker_flag: brokerFlag,
            module_name: "order",
            user_id: Number(sharedData?.ID || localStorage.getItem("id") || 0),
            email: userEmail,
          }),
        });

        const colOrderPayload = await colOrderResponse.json();
        const colData = colOrderPayload?.data;
        const apiColumns = colData?.columns;
        if (colData?.status === "success" && Array.isArray(apiColumns)) {
          const nextOrder = buildOrdersTableColumnOrderFromPms(apiColumns);
          if (nextOrder?.length) {
            console.log("ooooo Orders column order (PMS /columns/list):", nextOrder);
            setColumnOrder(nextOrder);
            setColumns(nextOrder);
            columnsToUse = nextOrder;
          } else {
            console.warn("PMS columns response empty after mapping, using default");
          }
        } else {
          console.warn("Failed to fetch column order from PMS, using default");
        }
      }
    } catch (error) {
      console.error("Error fetching column order:", error);
    }

    console.log("✅📉Columns is: ", columnsToUse);

    setProgress(10);

    const requestPayload = {
      email: userEmail,
      broker_flag: brokerFlag,
      ...(brokerFlag === "grow"
        ? { segment: "CASH", page: 0, page_size: 100 }
        : {}),
    };

    // Order Book/List
    fetch(`${pmsApiBaseUrl}/api/v1/get_orders`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(requestPayload),
    })
      .then((response) => {
        if (!response.ok) {
          throw new Error(`Network response was not ok. Status: ${response.status}`);
        }
        return response.json();
      })
      .then(async (data) => {
        if (fetchId !== ordersFetchIdRef.current) {
          return;
        }

        const apiPayload = data?.data || {};
        const apiStatus = String(apiPayload?.status || "").toLowerCase();

        if (apiStatus === "error") {
          const errMsg =
            apiPayload?.message ||
            apiPayload?.errors?.message ||
            "Failed to load orders.";
          console.error("Orders API error:", apiPayload);
          showErrorToast(errMsg);
          setOrderBook([]);
          setIsLoading(false);
          setProgress(100);
          return;
        }

        if (apiStatus && apiStatus !== "success") {
          console.warn("Unexpected orders API response:", data);
          setOrderBook([]);
          setIsLoading(false);
          setProgress(100);
          return;
        }

        const adapter = getOrderAdapter(brokerFlag);
        let holdings = apiPayload?.orders || data?.body?.OrderBookDetail || [];
        holdings = adapter.normalizeOrders(holdings);
        // console.log("📊 Total holdings received:", holdings.length);

        // ✨ Add ScripCode to each holding
        holdings = holdings.map((holding) => ({
          ...holding,
          ScripCode:
            brokerFlag === "grow"
              ? String(holding.ScripCode || "")
              : holding.ScripCode
                ? Number(holding.ScripCode)
                : 0,
        }));

        // 🔥 Fetch FullName for all holdings
        if (brokerFlag === "5paisa" && holdings.length > 0) {
          try {
            const stocksPayload = holdings.map((holding) => ({
              scripcode: holding.ScripCode,
              exch: holding.Exch?.charAt(0) || "",
              exchType: holding.ExchType?.charAt(0) || "",
              symbol: holding.ScripName,
            }));

            // console.log("📤 Sending FullName request...");
            // console.log("📤 Total stocks in payload:", stocksPayload.length);
            // console.log("📤 First 3 stocks:", stocksPayload.slice(0, 3));
            // console.log("📤 Full payload:", JSON.stringify({ stocks: stocksPayload }, null, 2));

            // Legacy getOrderBookFullNames disabled
            // const fullNameResponse = await fetch(
            //   "https://beta.infomanav.in/keep/zerodhaAPI/api.php?xAction=getOrderBookFullNames",
            //   {
            //     method: "POST",
            //     headers: {
            //       "Content-Type": "application/json",
            //       Accept: "application/json",
            //     },
            //     body: JSON.stringify({ stocks: stocksPayload }),
            //   }
            // );

            // // console.log("📡 Response status:", fullNameResponse.status);
            // // console.log("📡 Response ok:", fullNameResponse.ok);
            // // console.log("📡 Response status text:", fullNameResponse.statusText);
            // // console.log(
            // //   "📡 Response headers:",
            // //   Object.fromEntries(fullNameResponse.headers)
            // // );

            // // Get raw text first to see what's actually returned
            // const responseText = await fullNameResponse.text();
            // // console.log("📄 Raw response text length:", responseText.length);
            // // console.log("📄 Raw response (first 500 chars):", responseText.substring(0, 500));

            // // Try to parse as JSON
            // let fullNameResult;
            // try {
            //   fullNameResult = JSON.parse(responseText);
            //   // console.log("✅ Successfully parsed JSON");
            //   // console.log("✅ Parsed result:", fullNameResult);
            // } catch (parseError) {
            //   // console.error("❌ JSON parse error:", parseError.message);
            //   // console.error("🔍 Full response text:", responseText);
            //   throw new Error(`Invalid JSON response: ${parseError.message}`);
            // }

            // // Check if API returned success
            // if (fullNameResult.status === "success") {
            //   console.log("✅ API returned success status");

            //   if (fullNameResult.data && Array.isArray(fullNameResult.data)) {
            //     console.log(`✅ Received ${fullNameResult.count} FullName results`);
            //     console.log("📊 Sample results:", fullNameResult.data.slice(0, 3));

            //     // Merge FullName into holdings
            //     holdings = holdings.map((holding, index) => {
            //       const matchedStock = fullNameResult.data.find(
            //         (item) =>
            //           item.scripcode == holding.ScripCode &&
            //           item.exch === holding.Exch?.charAt(0) &&
            //           item.exchType === holding.ExchType?.charAt(0)
            //       );

            //       if (matchedStock) {
            //         console.log(
            //           `✅ [${index}] Matched ${holding.ScripName} (${holding.ScripCode}): ${matchedStock.fullName}`
            //         );
            //       } else {
            //         console.warn(
            //           `⚠️ [${index}] No match for ${holding.ScripName} (ScripCode: ${holding.ScripCode}, Exch: ${holding.Exch?.charAt(0)}, ExchType: ${holding.ExchType?.charAt(0)})`
            //         );
            //       }

            //       return {
            //         ...holding,
            //         FullName: matchedStock?.fullName || holding.ScripName || "N/A",
            //       };
            //     });

            //     console.log("✅ FullName data merged successfully into holdings");
            //     console.log("📦 Sample merged holding:", holdings[0]);
            //   } else {
            //     console.warn("⚠️ API returned success but data is missing or not an array");
            //     console.warn("⚠️ Full result:", fullNameResult);
            //   }
            // } else {
            //   console.warn("⚠️ API returned non-success status:", fullNameResult.status);
            //   console.warn("⚠️ API message:", fullNameResult.message);
            //   console.warn("⚠️ Full result:", fullNameResult);
            // }

            holdings = holdings.map((holding) => ({
              ...holding,
              FullName: holding.FullName || holding.ScripName || "N/A",
            }));
          } catch (error) {
            console.error("❌ Error in FullName fetch operation:", error);
            console.error("❌ Error name:", error.name);
            console.error("❌ Error message:", error.message);
            console.error("❌ Error stack:", error.stack);
            // Continue without FullName if API fails
            console.log("⚠️ Continuing without FullName data...");
          }
        } else {
          console.log("ℹ️ No holdings to fetch FullName for");
        }

        if (fetchId !== ordersFetchIdRef.current) {
          return;
        }

        console.log("📦 Final holdings before setting state:", holdings.length);
        setOrderBook(holdings);

        // Connect global WS service and subscribe order feed symbols
        if (
          brokerFlag === "5paisa" &&
          (apiStatus === "success" || data?.body?.Message === "Success") &&
          holdings.length > 0
        ) {
          console.log("🔌 Setting up global WebSocket subscriptions...");
          subscribeOrderFeeds(holdings);
        } else if (brokerFlag === "angelone" && apiStatus === "success" && holdings.length > 0) {
          console.log("🔌 Setting up AngelOne WebSocket subscriptions...");
          subscribeAngelOrderFeeds(holdings);
        } else {
          console.log("ℹ️ Skipping WebSocket setup (no holdings or not success)");
        }

        setTimeout(() => {
          if (fetchId !== ordersFetchIdRef.current) return;
          setIsLoading(false);
          console.log("✅ Loading complete");
        }, 1500);
        setProgress(100);
      })
      .catch((error) => {
        console.error("❌ There was a problem with the fetch operation:", error);
        console.error("❌ Error details:", {
          name: error.name,
          message: error.message,
          stack: error.stack,
        });
        if (fetchId !== ordersFetchIdRef.current) return;
        setOrderBook([]);
        setIsLoading(false);
        setProgress(0);
      });
  };



  const convertDate = (dateString) => {
    if (!dateString) return "-";
    const raw = String(dateString);
    const match = raw.match(/\/Date\((\d+)(?:[+-]\d+)?\)\//);
    const timestamp = match ? parseInt(match[1], 10) : Date.parse(raw);
    if (!Number.isFinite(timestamp)) return "-";
    const dateObject = new Date(timestamp);
    // return `${dateObject.toLocaleDateString()} ${dateObject.toLocaleTimeString()}`;
    return `${dateObject.toLocaleDateString()}`;
  };

  const convertDate1 = (dateString) => {
    if (!dateString) return "-";
    const raw = String(dateString);
    const match = raw.match(/\/Date\((\d+)(?:[+-]\d+)?\)\//);
    const timestamp = match ? parseInt(match[1], 10) : Date.parse(raw);
    if (!Number.isFinite(timestamp)) return "-";
    const dateObject = new Date(timestamp);
    // return `${dateObject.toLocaleDateString()} ${dateObject.toLocaleTimeString()}`;
    return `${dateObject.toLocaleTimeString()}`;
  };

  const formatNumber = (num) => {
    if (num === null || num === undefined) return "-";
    const number = parseFloat(num);
    if (isNaN(number)) return "-";

    return number.toLocaleString("en-IN", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  };

  const getStatusClass = (orderStatus) => {
    const status = String(orderStatus || "").trim().toLowerCase();

    if (!status) return "status_default";
    if (status.includes("cancel")) return "status_cancel";
    if (
      status.includes("fully executed") ||
      status.includes("complete") ||
      status === "success"
    ) {
      return "status_success";
    }
    if (
      status.includes("rejected") ||
      status.includes("failed") ||
      status.includes("invalid") ||
      status.includes("error")
    ) {
      return "status_rejected";
    }
    if (status.includes("pending") || status.includes("scheduled")) {
      return "status_pending";
    }
    if (status.includes("partial")) return "status_partial";

    return "status_default";
  };

  function getReason(order) {
    if (order?.Reason && order.Reason.trim() !== "") {
      return order.Reason; // 1️⃣ Primary reason from API
    }

    switch (order?.OrderStatus) {
      case "Pending":
        return "Order is waiting in queue"; // default/fallback
      case "ACKED":
      case "Scheduled (AMO)":
        return "Order is scheduled for market open (AMO)";
      case "Cancelled":
        return "Order was cancelled"; // default/fallback
      case "Completed":
      case "Fully Executed":
        return "Order completed successfully";
      default:
        return order?.OrderStatus || "Unknown";
    }
  }



  const wrapText = (text, maxChar) => {
    if (!text) return '';

    // Split the text by words
    const words = text.split(/\s+/);

    // Wrap the text after a certain number of characters
    let wrappedText = '';
    let currentLine = '';

    words.forEach((word, index) => {
      if ((currentLine + word).length <= maxChar) {
        currentLine += word + ' ';
      } else {
        wrappedText += currentLine.trim() + '<br>';
        currentLine = word + ' ';
      }

      // Add a newline at the end if it's the last word
      if (index === words.length - 1) {
        wrappedText += currentLine.trim();
      }
    });

    return wrappedText;
  };


  const subscribeOrderFeeds = (orders = []) => {
    const marketFeedItems = orders
      .map((order) => ({
        Exch: order?.Exch,
        ExchType: order?.ExchType,
        ScripCode: order?.ScripCode,
      }))
      .filter((item) => item.Exch && item.ExchType && item.ScripCode);

    if (marketFeedItems.length) {
      fivePaisaWebSocketService.subscribeMarketFeed(marketFeedItems);
    }
  };

  const subscribeAngelOrderFeeds = (orders = []) => {
    if (angelOrderSubscriptionsRef.current.length) {
      angelOneWebSocketService.unsubscribeMarketFeed(
        angelOrderSubscriptionsRef.current,
      );
      angelOrderSubscriptionsRef.current = [];
    }

    const seenTokens = new Set();
    const feedItems = [];

    orders.forEach((order) => {
      const token = String(order?.token || order?.ScripCode || "").trim();
      if (!token || seenTokens.has(token)) return;

      const exchangeType = getAngelExchangeTypeFromHolding({
        Exch: order?.Exch,
        ExchType: order?.ExchType,
        ExchSeg:
          order?.Exch === "M"
            ? "MCX"
            : order?.Exch === "B"
              ? "BSE"
              : "NSE",
        InstrumentType:
          order?.instrumenttype ||
          order?.InstrumentType ||
          order?.segment ||
          "",
      });

      if (!exchangeType) return;

      seenTokens.add(token);
      feedItems.push({ exchangeType, token });
    });

    if (!feedItems.length) return;

    angelOneWebSocketService.connect();
    for (let i = 0; i < feedItems.length; i += 50) {
      angelOneWebSocketService.subscribeMarketFeed(feedItems.slice(i, i + 50));
    }
    angelOrderSubscriptionsRef.current = feedItems;
  };




  // const sendMessageToWebSocket = (holding, clientCode, status = "Watchlist") => {
  //   const currentWs = wsRef.current;

  //   if (!currentWs || currentWs.readyState !== WebSocket.OPEN) {
  //     console.warn("WebSocket is not connected, readyState:", currentWs?.readyState);
  //     return;
  //   }

  //   let ScripCode = 0;
  //   if (status === "Search") {
  //     ScripCode = holding.Scripcode;
  //   } else {
  //     ScripCode = holding.ScripCode;
  //   }

  //   const priceMessage = {
  //     Method: "MarketFeedV3",
  //     Operation: "Subscribe",
  //     ClientCode: clientCode,
  //     MarketFeedData: [
  //       {
  //         Exch: holding.Exch,
  //         ExchType: holding.ExchType,
  //         ScripCode: ScripCode,
  //       },
  //     ],
  //   };


  //   try {
  //     currentWs.send(JSON.stringify(priceMessage));
  //     // currentWs.send(JSON.stringify(depthMessage));
  //     console.log(`✅ Subscribed to ${holding.name} (${ScripCode})`);

  //     if (!subscribedScripCodes.current) {
  //       subscribedScripCodes.current = new Set();
  //     }
  //     subscribedScripCodes.current.add(ScripCode);
  //   } catch (error) {
  //     console.error("Error sending WebSocket message:", error);
  //   }
  // };





  // const setupWebSocketMessageHandler = (websocket) => {
  //   const ws = websocket || wsRef.current;

  //   if (ws && ws.readyState === WebSocket.OPEN) {
  //     ws.onmessage = (event) => {
  //       try {
  //         const receivedData = JSON.parse(event.data);

  //         // Handle heartbeat response
  //         if (receivedData.Method === "Heartbeat") {
  //           console.log("💗 Heartbeat response received");
  //           return;
  //         }

  //         // Handle live price data
  //         if (Array.isArray(receivedData) && receivedData[0]) {
  //           const data = receivedData[0];
  //           handleLivePriceUpdate(data);
  //         }

  //       } catch (error) {
  //         console.error("❌ Error processing WebSocket message:", error);
  //       }
  //     };
  //   }
  // };




  // Handle Live Price Updates





  // const handleLivePriceUpdate = (data) => {
  //   const { Token: scriptCode, LastRate: lastRateValue, PClose, Volume } = data;

  //   if (!scriptCode || lastRateValue === undefined) return;

  //   if (!livePriceCache.current) livePriceCache.current = {};
  //   if (!livePCloseCache.current) livePCloseCache.current = {};
  //   if (!liveDiffCache.current) liveDiffCache.current = {};
  //   if (!liveChangePercentageCache.current) liveChangePercentageCache.current = {};
  //   if (!liveVolumeCache.current) liveVolumeCache.current = {};
  //   if (!livePLCache.current) livePLCache.current = {};

  //   livePriceCache.current[scriptCode] = lastRateValue;
  //   livePCloseCache.current[scriptCode] = PClose;
  //   liveDiffCache.current[scriptCode] = lastRateValue - PClose;
  //   liveChangePercentageCache.current[scriptCode] = ((lastRateValue - PClose) / PClose) * 100;
  //   liveVolumeCache.current[scriptCode] = Volume;

  //   const totalPL = lastRateValue - PClose;
  //   livePLCache.current[scriptCode] = `${totalPL.toFixed(2)} (${liveChangePercentageCache.current[scriptCode].toFixed(2)}%)`;

  //   setLiveUpdateTrigger((prev) => prev + 1);
  //   console.log(`📈 Price Update: ${scriptCode} = ₹${lastRateValue}`);
  // };


  // 🔥 Heartbeat function to keep connection alive

  const handleLivePriceUpdate = (data) => {
    // console.log("🔵 STEP 6: Processing Live Price Update");

    const { Token: scriptCode, LastRate: lastRateValue, PClose, Volume } = data;

    if (!scriptCode || lastRateValue === undefined) return;


    // Initialize caches if not exist
    if (!livePriceCache.current) livePriceCache.current = {};
    if (!livePCloseCache.current) livePCloseCache.current = {};
    if (!liveDiffCache.current) liveDiffCache.current = {};
    if (!liveChangePercentageCache.current) liveChangePercentageCache.current = {};
    if (!liveVolumeCache.current) liveVolumeCache.current = {};

    // Calculate change
    const previousClose = PClose || livePCloseCache.current[scriptCode];
    const change = previousClose ? lastRateValue - previousClose : 0;
    const changePercent = previousClose ? ((change / previousClose) * 100) : 0;

    // Update all caches
    livePriceCache.current[scriptCode] = lastRateValue;
    livePCloseCache.current[scriptCode] = previousClose;
    liveDiffCache.current[scriptCode] = change;
    liveChangePercentageCache.current[scriptCode] = changePercent;
    liveVolumeCache.current[scriptCode] = Volume;

    // Force UI update immediately
    setLiveUpdateTrigger((prev) => prev + 1);

    // console.log(`📊 Price Update: ${scriptCode} = ₹${lastRateValue} (Change: ${change.toFixed(2)})`);
  };


  // Handle Market Depth Updates
  const handleMarketDepthUpdate = (depthData) => {
    try {
      console.log("📊 Depth Update:", depthData);

      // Example: update React state with bid/ask data
      // Adjust based on the exact payload shape you receive
      setMarketDepth(depthData);

    } catch (error) {
      console.error("❌ Error processing Market Depth:", error);
    }
  };









  const handlePreview = () => setIsPreview(true);
  const handleGoBack = () => setIsPreview(false);




  const SettingBuyModalDetails = (stockData, Type) => {
    setBuySellModalDetails(prevState => ({
      ...prevState,
      FullName: stockData.FullName || stockData.ScripName || '',
      Symbol: stockData.Symbol || stockData.symbol || stockData.ScripName || '',
      Type: Type,
      OrderQty: 1,
      LimitPrice: 0,
      order: stockData // store complete order object
    }));
  };

  const getPmsResponseMessage = (payload) => {
    if (!payload) return "";
    if (typeof payload === "string") return payload;
    return (
      payload?.message ||
      payload?.Message ||
      payload?.data?.message ||
      payload?.data?.Message ||
      ""
    );
  };

  const isOrderActionable = (order) => {
    const status = String(order?.OrderStatus || order?.order_status || "")
      .toLowerCase()
      .trim();
    return !(
      status.includes("cancel") ||
      status.includes("reject") ||
      status.includes("executed") ||
      status.includes("complete")
    );
  };

  const openModifyOrderModal = (order) => {
    const defaultPrice = Number(order?.price ?? order?.Price ?? order?.Rate ?? 0);
    const defaultTrigger = Number(
      order?.trigger_price ?? order?.StopLossPrice ?? order?.SLTriggerRate ?? 0,
    );
    const defaultOrderType = String(
      order?.order_type || (defaultPrice > 0 ? "LIMIT" : "MARKET"),
    ).toUpperCase();
    const defaultSegment = String(order?.segment || "CASH").toUpperCase();

    setModifyOrderModalDetails({
      Symbol: order?.Symbol || order?.ScripName || "",
      FullName: order?.FullName || order?.ScripName || "",
      quantity: Number(order?.Qty ?? order?.quantity ?? 0),
      price: Number.isFinite(defaultPrice) ? defaultPrice : 0,
      triggerPrice: Number.isFinite(defaultTrigger) ? defaultTrigger : 0,
      orderType: defaultOrderType,
      segment: defaultSegment,
      exchOrderID: String(order?.ExchOrderID || order?.exchange_order_id || ""),
      growwOrderId: String(order?.groww_order_id || order?.BrokerOrderId || ""),
      angelOrderId: String(
        order?.orderid || order?.BrokerOrderId || order?.broker_order_id || "",
      ),
      variety: normalizeAngelOneVariety(order?.variety),
      producttype: String(order?.producttype || order?.productType || "DELIVERY").toUpperCase(),
      duration: String(order?.duration || "DAY").toUpperCase(),
      order,
    });
  };

  const submitModifyOrder = async () => {
    if (!modifyOrderModalDetails.order) return;
    if (!isOrderActionable(modifyOrderModalDetails.order)) {
      toast.info("Only pending/open orders can be modified.");
      return;
    }

    const isGrowBroker = activeBrokerFlag === "grow";
    if (isAngelOneBroker && !modifyOrderModalDetails.angelOrderId) {
      toast.error("AngelOne order id is missing for this order.");
      return;
    }
    if (isGrowBroker && !modifyOrderModalDetails.growwOrderId) {
      toast.error("Groww order id is missing for this order.");
      return;
    }
    if (
      !isGrowBroker &&
      !isAngelOneBroker &&
      !modifyOrderModalDetails.exchOrderID
    ) {
      toast.error("Exchange order id is missing for this order.");
      return;
    }

    setIsModifySubmitting(true);
    try {
      const selectedOrder = modifyOrderModalDetails.order || {};
      const quantity = Number(
        modifyOrderModalDetails.quantity ||
        selectedOrder?.Qty ||
        selectedOrder?.quantity ||
        0,
      );
      const price = Number(
        modifyOrderModalDetails.price ??
        selectedOrder?.Rate ??
        selectedOrder?.price ??
        0,
      );
      const triggerPrice = Number(
        modifyOrderModalDetails.triggerPrice ??
        selectedOrder?.SLTriggerRate ??
        selectedOrder?.trigger_price ??
        0,
      );
      const orderType = String(
        modifyOrderModalDetails.orderType ||
        selectedOrder?.order_type ||
        "LIMIT",
      ).toUpperCase();
      const segment = String(
        modifyOrderModalDetails.segment || selectedOrder?.segment || "CASH",
      ).toUpperCase();

      const result = await modifyOrderViaPms({
        pmsApiBaseUrl,
        sharedData,
        quantity,
        price,
        triggerPrice,
        orderType,
        segment,
        growwOrderId: modifyOrderModalDetails.growwOrderId,
        exchOrderID: modifyOrderModalDetails.exchOrderID,
        stopLossPrice: triggerPrice,
        watchlist: selectedOrder,
        angelOrderId: modifyOrderModalDetails.angelOrderId,
        variety: modifyOrderModalDetails.variety,
        producttype: modifyOrderModalDetails.producttype,
        duration: modifyOrderModalDetails.duration,
      });

      if (!result.ok) {
        toast.error(
          getPmsResponseMessage(result.data) || result.error || "Modify order failed",
        );
        return;
      }

      toast.success(getPmsResponseMessage(result.data) || "Order modified successfully");
      orderBookList();
      document.getElementById("closeModifyOrderModalBtn")?.click();
    } catch (error) {
      toast.error(error?.message || "Modify order failed");
    } finally {
      setIsModifySubmitting(false);
    }
  };

  const openCancelOrderModal = (order) => {
    if (!isOrderActionable(order)) {
      toast.info("Only pending/open orders can be cancelled.");
      return;
    }
    setCancelOrderModalDetails({
      show: true,
      order,
      symbol: order?.Symbol || order?.ScripName || order?.FullName || "-",
    });
  };

  const cancelExistingOrder = async () => {
    const order = cancelOrderModalDetails.order;
    if (!order) return;

    const growwOrderId = String(order?.groww_order_id || order?.BrokerOrderId || "");
    const exchOrderID = String(order?.ExchOrderID || order?.exchange_order_id || "");
    const angelOrderId = String(
      order?.orderid || order?.BrokerOrderId || order?.broker_order_id || "",
    );
    const variety = normalizeAngelOneVariety(order?.variety);
    const segment = String(order?.segment || "CASH").toUpperCase();
    const isGrowBroker = activeBrokerFlag === "grow";

    if (isAngelOneBroker && !angelOrderId) {
      toast.error("AngelOne order id is missing for this order.");
      return;
    }
    if (isGrowBroker && !growwOrderId) {
      toast.error("Groww order id is missing for this order.");
      return;
    }
    if (!isGrowBroker && !isAngelOneBroker && !exchOrderID) {
      toast.error("Exchange order id is missing for this order.");
      return;
    }

    setIsCancelSubmitting(true);
    try {
      const result = await cancelOrderViaPms({
        pmsApiBaseUrl,
        sharedData,
        segment,
        growwOrderId,
        exchOrderID,
        angelOrderId,
        variety,
        order,
      });

      if (!result.ok) {
        toast.error(
          getPmsResponseMessage(result.data) || result.error || "Cancel order failed",
        );
        return;
      }

      toast.success(getPmsResponseMessage(result.data) || "Order cancelled successfully");
      orderBookList();
      document.getElementById("closeCancelOrderModalBtn")?.click();
      setCancelOrderModalDetails({ show: false, order: null, symbol: "" });
    } catch (error) {
      toast.error(error?.message || "Cancel order failed");
    } finally {
      setIsCancelSubmitting(false);
    }
  };




  //----------------------------//  Buy-Sell Functions //----------------------------//`

  // Placing Order steps:
  // CDSL Record Count Function
  // CDSL Record Count Function with Debug Logging
  async function getCDSLRecordCount(userId) {
    console.log("🔍 getCDSLRecordCount - Starting function");
    console.log("📋 Input userId:", userId);

    const apiUrl = "https://beta.infomanav.in/keep/zerodhaAPI/api.php?xAction=getCDSLRecord";
    console.log("🌐 API URL:", apiUrl);

    try {
      console.log("📤 Preparing to send request...");

      const requestBody = { userId: userId };
      console.log("📦 Request body:", JSON.stringify(requestBody));

      const response = await fetch(apiUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(requestBody),
      });

      console.log("📥 Response received");
      console.log("✅ Response status:", response.status);
      console.log("📊 Response statusText:", response.statusText);
      console.log("🔗 Response OK:", response.ok);

      if (!response.ok) {
        console.error("❌ Response not OK - throwing error");
        throw new Error(`Error: ${response.status} - ${response.statusText}`);
      }

      console.log("🔄 Parsing response as JSON...");
      const data = await response.json();
      console.log("📋 Raw response data:", data);

      const count = data.count;
      console.log("🔢 Extracted count:", count);
      console.log("✅ getCDSLRecordCount - Function completed successfully");

      return count;
    } catch (error) {
      console.error("💥 Error in getCDSLRecordCount:");
      console.error("❌ Error message:", error.message);
      console.error("📊 Full error object:", error);
      console.log("🔄 Returning default value: 0");

      // Return 0 as default if error occurs
      return 0;
    }
  }



  const fetchDataPortfolio = (watchlist, transactionSide, status) => {
    console.log("🚀 fetchDataPortfolio - Starting function");
    console.log("📊 Input parameters:");
    console.log("   - watchlist:", watchlist);
    console.log("   - transactionSide:", transactionSide);
    console.log("   - status:", status);

    const Exch = watchlist.Exch;
    console.log("🏛️ Exchange (Exch):", Exch);

    const ExchType = Exch === "M" ? "D" : "C";
    console.log("📈 Exchange Type (ExchType):", ExchType, `(${Exch === "M" ? "Derivative" : "Cash"})`);

    const Name = watchlist.ScripName;
    console.log("🏢 Company Name:", Name);

    let Scripcode = "";

    if (status === "Portfolio") {
      // ✨ Use Number conversion for ScripCode with fallback
      Scripcode = Number(watchlist.ScripCode) || Number(watchlist.scripcode);
      console.log("📋 Portfolio mode - Using pre-calculated ScripCode:");
      console.log(`   - Exchange is ${watchlist.Exch}, ScripCode: ${Scripcode}`);
      console.log(`   - NseCode: ${watchlist.NseCode}, BseCode: ${watchlist.BseCode}`);
      console.log(`   - Selected Scripcode: ${Scripcode}`);
    } else {
      Scripcode = watchlist.Scripcode;
      console.log("📝 Watchlist mode - Using direct Scripcode:", Scripcode);
    }

    let watchlist_price = "0";
    let min_qty = "0";

    // ✅ Get values from modal state instead of DOM
    watchlist_price =
      orderType === "Market Order"
        ? "0"
        : buySellModalDetails.LimitPrice.toString();
    min_qty = buySellModalDetails.OrderQty.toString();

    console.log("💰 Price & Quantity from modal:");
    console.log("   - Execution (Market/Limit):", orderType);
    console.log("   - Price (watchlist_price):", watchlist_price);
    console.log("   - Quantity (min_qty):", min_qty);
    console.log("   - buySellModalDetails:", buySellModalDetails);

    const userId = sharedData.ID;
    console.log("👤 User ID:", userId);
    console.log("📱 sharedData object:", sharedData);

    const order_type = transactionSide;
    console.log("📊 Final order_type (Buy/Sell):", order_type);

    // Create a JavaScript object with the request data
    const requestData = {
      ExchType,
      order_type,
      Exch,
      Name,
      Scripcode,
      watchlist_price,
      min_qty,
      userId,
    };

    console.log("📦 Complete request data object:");
    console.log(JSON.stringify(requestData, null, 2));

    // ✅ CDSL validation for 5paisa Sell only (not Grow / AngelOne)
    if (transactionSide === "Sell" && isAngelOneBroker) {
      console.log("🛑 SELL order (AngelOne) - skipping CDSL validation");
      executeBuySellOrderPortfolio(
        requestData,
        watchlist,
        transactionSide,
        status,
      );
    } else if (transactionSide === "Sell") {
      console.log("🛑 SELL order detected - checking CDSL validation");
      getCDSLRecordCount(userId)
        .then((count) => {
          console.log("✅ CDSL validation completed");
          console.log("🔢 CDSL Record Count:", count);

          if (count === 0) {
            console.error("❌ CDSL Record Count is 0 - cannot place sell order");
            console.log("🚫 Sell order blocked - CDSL setup required");
            // TODO: Show error modal - user needs to complete CDSL setup
            // document.getElementById("modalClick").click();
          } else {
            console.log("✅ CDSL validation passed - proceeding with sell order");
            executeBuySellOrderPortfolio(requestData, watchlist, transactionSide, status);
          }
        })
        .catch((error) => {
          console.error("💥 Error getting CDSL count:", error);
          console.log("⚠️ CDSL validation failed - proceeding with order anyway");
          // Proceed with order anyway or show error
          executeBuySellOrderPortfolio(requestData, watchlist, transactionSide, status);
        });
    } else if (transactionSide === "Buy") {
      console.log("💚 BUY order detected - no CDSL validation needed");
      console.log("🚀 Proceeding directly to order execution");
      executeBuySellOrderPortfolio(requestData, watchlist, transactionSide, status);
    } else {
      console.error("❓ Unknown transaction side:", transactionSide);
    }

    console.log("🏁 fetchDataPortfolio - Function end");
    setTriggerUpdate(x => x + 1);
  };




  // Function to handle the checkSell value
  // const executeBuySellOrderPortfolio = (requestData, watchlist, orderType, status) => {
  //   console.log("🔧 executeBuySellOrderPortfolio - Starting function");
  //   console.log("📥 Input parameters:");
  //   console.log("   - requestData:", requestData);
  //   console.log("   - watchlist:", watchlist);
  //   console.log("   - orderType:", orderType);
  //   console.log("   - status:", status);

  //   const apiUrl = "https://beta.infomanav.in/keep/zerodhaAPI/api.php?xAction=addFivePaisaOrder";
  //   console.log("🌐 API URL:", apiUrl);
  //   console.log("📤 Preparing to send order to database...");

  //   // Send a POST request using fetch
  //   fetch(apiUrl, {
  //     method: "POST",
  //     headers: {
  //       "Content-Type": "application/json",
  //     },
  //     body: JSON.stringify(requestData),
  //   })
  //     .then((response) => {
  //       console.log("📥 Database response received");
  //       console.log("✅ Response status:", response);
  //       console.log("✅ Response status:", response.status);
  //       console.log("📊 Response statusText:", response.statusText);
  //       console.log("🔗 Response OK:", response.ok);

  //       if (!response.ok) {
  //         console.error("❌ Database response not OK");
  //         throw new Error("Network response was not ok");
  //       }

  //       // console.log("🔄 Parsing database response as JSON...");
  //       // return response.json();
  //       console.log("🔄 Parsing database response as JSON...");
  // return response.text().then(text => {
  //   console.log("📄 Raw response text:", text);

  //   // Check if response starts with HTML error
  //   if (text.trim().startsWith('<br />') || text.trim().startsWith('<')) {
  //     console.error("❌ Server returned HTML error instead of JSON");
  //     console.error("📄 HTML Error content:", text);
  //     throw new Error("Server returned HTML error: " + text.substring(0, 100) + "...");
  //   }

  //   try {
  //     const jsonData = JSON.parse(text);
  //     console.log("✅ Successfully parsed JSON:", jsonData);
  //     return jsonData;
  //   } catch (parseError) {
  //     console.error("❌ JSON Parse Error:", parseError);
  //     console.error("📄 Failed to parse:", text);
  //     throw new Error("Invalid JSON response from server: " + text.substring(0, 100));
  //   }
  // });

  //     })
  //     .then((data) => {
  //       console.log("📋 Database response data:", data);

  //       // Handle the response data here
  //       if (data.message === "No data found in table") {
  //         console.log("⚠️ No data found in order table");
  //         console.log("🛑 Order execution stopped");
  //       } else {
  //         console.log("✅ Database order saved successfully");
  //         console.log("🚀 Proceeding to place order with 5paisa API");
  //         clickPlaceOrderPortfolio(watchlist, orderType, status);

  //         console.log("🔄 Starting holdings refresh...");

  //         const holdingsRefreshData = {
  //           AccessToken: sharedData.AccessToken,
  //           ClientCode: sharedData.ClientCode,
  //           api_key: sharedData.api_key,
  //           userId: sharedData.ID,
  //         };

  //         console.log("📦 Holdings refresh request data:", holdingsRefreshData);

  //         // ✅ Refresh holdings in DB
  //         fetch("https://beta.infomanav.in/keep/zerodhaAPI/api.php?xAction=getHoldingData", {
  //           method: "POST",
  //           headers: { "Content-Type": "application/json" },
  //           body: JSON.stringify(holdingsRefreshData),
  //         })
  //           .then((res) => {
  //             console.log("📥 Holdings refresh response received");
  //             console.log("✅ Holdings refresh status:", res.status);
  //             console.log("🔗 Holdings refresh OK:", res.ok);
  //             return res.json();
  //           })
  //           .then((holdingsData) => {
  //             console.log("✅ Holdings refreshed successfully");
  //             console.log("📊 Updated holdings data:", holdingsData);
  //           })
  //           .catch((err) => {
  //             console.error("💥 Error refreshing holdings:");
  //             console.error("❌ Holdings refresh error:", err);
  //           });
  //       }
  //     })
  //     .catch((error) => {
  //       console.error("💥 Database operation failed:");
  //       console.error("❌ Fetch error message:", error.message);
  //       console.error("📊 Full error object:", error);
  //       console.log("🛑 Order execution terminated due to database error");
  //     });

  //   console.log("🏁 executeBuySellOrderPortfolio - Function end (async operations continue)");
  // };
  const executeBuySellOrderPortfolio = (requestData, watchlist, transactionSide, status) => {
    console.log("🔧 executeBuySellOrderPortfolio - Starting function");
    console.log("📥 Input parameters:");
    console.log("   - requestData:", requestData);
    console.log("   - watchlist:", watchlist);
    console.log("   - transactionSide:", transactionSide);
    console.log("   - status:", status);

    // ✅ REMOVED: Database call - now happens AFTER order placement
    console.log("🚀 Proceeding to PMS place_order");
    clickPlaceOrderPortfolio(requestData, watchlist, transactionSide, status);

    console.log("🏁 executeBuySellOrderPortfolio - Function end");
  };



  const clickPlaceOrderPortfolio = (
    requestData,
    watchlist,
    transactionSide,
    status,
  ) => {
    const executionType = orderType;

    console.log("🎯 clickPlaceOrderPortfolio — PMS /api/v1/place_order");
    console.log(
      "   transactionSide:",
      transactionSide,
      "executionType:",
      executionType,
    );

    placeOrderViaPms({
      pmsApiBaseUrl,
      sharedData,
      watchlist,
      listStatus: status,
      transactionSide,
      executionType,
      qty: buySellModalDetails.OrderQty,
      limitPrice: buySellModalDetails.LimitPrice,
    }).then((result) => {
      if (result.error) {
        console.error("💥 place_order:", result.error);
        showErrorToast("Failed to place order. Please try again.");
        setTimeout(() => navigate("/Order"), 1500);
        return;
      }

      const data = result.data;
      console.log("📋 place_order response:", JSON.stringify(data, null, 2));

      const normalized = normalizePlaceOrderResponse(data);

      if (!result.ok) {
        const msg = normalized.message || "Failed to place order";
        showErrorToast(msg);
        setTimeout(() => navigate("/Order"), 1500);
        return;
      }

      const message = normalized.message || "";
      const rmsResponseCode = normalized.rmsResponseCode;
      const brokerOrderID = normalized.brokerOrderID;
      const exchangeOrderID = normalized.exchangeOrderID;

      if (
        isFivePaisaBroker(sharedData) &&
        (rmsResponseCode === -162 ||
          message.toLowerCase().includes("sell authorization not received") ||
          message.toLowerCase().includes("authorization not received"))
      ) {
        toast.error("CDSL authorization required to place sell orders");
        if (transactionSide === "Buy") {
          document.getElementById("buyModalClick")?.click();
        } else if (transactionSide === "Sell") {
          document.getElementById("sellModalClick")?.click();
        }
        setTimeout(() => {
          document.getElementById("openCDSLModalBtn")?.click();
        }, 500);
        return;
      }

      if (normalized.headStatus !== "0") {
        showErrorToast(message || "Failed to place order");
        setTimeout(() => navigate("/Order"), 1500);
        return;
      }

      runPlaceOrderAcceptedFlow({
        transactionSide,
        tr,
        showSuccessToast,
        navigate,
      });
    });

    console.log(
      "🏁 clickPlaceOrderPortfolio - Function end (async PMS call continues)",
    );
  };

  const refreshHoldings = () => {
    console.log("🔄 Starting holdings refresh...");



  };

  const updateMargin = () => {
    console.log("💰 updateMargin - Starting margin update");

    // Get sharedData from localStorage
    const saved = JSON.parse(localStorage.getItem("sharedData"));

    if (!saved || !saved.accessToken || !saved.api_key || !saved.ClientCode) {
      console.error("❌ Missing required data for margin update");
      return;
    }

    const apiUrl = "https://Openapi.5paisa.com/VendorsAPI/Service1.svc/V4/Margin";
    console.log("🌐 Margin API URL:", apiUrl);

    const requestBody = {
      head: {
        key: saved.api_key
      },
      body: {
        ClientCode: saved.ClientCode
      }
    };

    console.log("📦 Margin request body:", requestBody);

    const requestHeaders = {
      "Authorization": `Bearer ${saved.accessToken}`,
      "Content-Type": "application/json"
    };

    console.log("📤 Sending margin update request...");

    fetch(apiUrl, {
      method: "POST",
      headers: requestHeaders,
      body: JSON.stringify(requestBody)
    })
      .then((response) => {
        console.log("📥 Margin API response received");
        console.log("✅ Response status:", response.status);

        if (!response.ok) {
          console.error("❌ Margin API response not OK");
          throw new Error("Margin API response was not ok");
        }

        return response.json();
      })
      .then((data) => {
        console.log("📋 Margin API Response Data:");
        console.log(JSON.stringify(data, null, 2));

        // Extract NetAvailableMargin from response
        const equityMargin = data.body?.EquityMargin?.[0];

        if (equityMargin && equityMargin.NetAvailableMargin !== undefined) {
          const netAvailableMargin = equityMargin.NetAvailableMargin;
          console.log("💵 NetAvailableMargin:", netAvailableMargin);

          // Update sharedData in localStorage
          const currentSharedData = JSON.parse(localStorage.getItem("sharedData"));
          currentSharedData.margin = netAvailableMargin;

          localStorage.setItem("sharedData", JSON.stringify(currentSharedData));
          console.log("✅ Margin updated in localStorage:", netAvailableMargin);

          // 🆕 Optional: Update state if you have margin in component state
          // setSharedData(currentSharedData); // Uncomment if you use state

          // Show success toast
          console.log("💰 Available Margin: ₹" + netAvailableMargin.toFixed(2));

        } else {
          console.error("❌ NetAvailableMargin not found in response");
          console.error("📊 EquityMargin data:", equityMargin);
        }
      })
      .catch((error) => {
        console.error("💥 Margin API call failed:");
        console.error("❌ Error type:", error.name);
        console.error("❌ Error message:", error.message);
        console.error("📊 Full error object:", error);
      });

    console.log("🏁 updateMargin - Function end (async call continues)");
  };






  const getPriceForCalculation = () => {
    if (orderType === "Market Order") {
      const cacheKey = getOrderLivePriceCacheKey(buySellModalDetails.order);
      const livePrice = livePriceCache.current[cacheKey];
      return livePrice || buySellModalDetails.order?.CurrentPrice || buySellModalDetails.order?.initialPrice || 0;
    } else {
      // Use limit price for Limit orders
      return buySellModalDetails.LimitPrice || 0;
    }
  };







  //----------------------------// End Buy-Sell Functions //----------------------------//`


  //----------------------------//  Market Depth Functions //----------------------------//`
  // Function to subscribe to MarketDepth for a given stock
  // 🔥 Subscribe to Market Depth (Order Book) for a stock
  const subscribeToMarketDepth = (holding) => {
    if (!holding) return;

    if (activeBrokerFlag === "angelone") {
      const userId =
        Number(localStorage.getItem("id")) || Number(sharedData?.ID) || 0;
      angelOneMarketDepthService.openDepth(holding, {
        pmsApiBaseUrl,
        userId,
      });
      return;
    }

    if (activeBrokerFlag === "5paisa") {
      fivePaisaWebSocketService.subscribeMarketDepth([
        {
          Exch: holding.Exch,
          ExchType: holding.ExchType,
          ScripCode: holding.ScripCode,
        },
      ]);
    }
  };

  const unsubscribeFromMarketDepth = () => {
    if (activeBrokerFlag === "angelone") {
      angelOneMarketDepthService.closeDepth();
      return;
    }

    const MDStock = currentMDModalRef.current;
    if (activeBrokerFlag === "5paisa" && MDStock) {
      fivePaisaWebSocketService.unsubscribeMarketDepth([
        {
          Exch: MDStock.Exch,
          ExchType: MDStock.ExchType,
          ScripCode: MDStock.ScripCode,
        },
      ]);
    }
  };




  // Function to run when modal closes

  const handleModalClose = () => {
    console.log("📉📉📉 MarketDepth modal closed!");

    if (currentMDModalRef.current) {
      console.log("📉 Unsubscribing to:", currentMDModalRef.current);
      unsubscribeFromMarketDepth();
      console.log("📉 Unsubscribed");

      // Clear the ref so it doesn't hold stale data
      currentMDModalRef.current = null;
      setcurrentMDModal(null);
      setMarketDepth([]);
      setShowMarketDepth(false);
    }
  };








  
  const closeMarketDepthPopover = () => {
    unsubscribeFromMarketDepth();
    setcurrentMDModal(null);
    setMarketDepth([]);
    setShowMarketDepth(false);
  };

  //----------------------------// End market Depth Functions //----------------------------//`



  //----------------------------// Drag-N-Drop Functions //----------------------------//
  // Save updated ordering to PMS (module_name must stay lowercase: "order")
  const saveColumnOrder = async (newOrder) => {
    try {
      const userEmail =
        sharedData?.Email_ID ||
        sharedData?.email ||
        localStorage.getItem("email") ||
        "";
      if (!userEmail) {
        console.warn("saveColumnOrder: missing email, skipping PMS reorder");
        return;
      }

      const pmsBrokerFlag = toPmsBrokerFlag(activeBrokerFlag);
      const columnsPayload = [];
      let sortOrder = 1;
      for (const label of newOrder) {
        if (label === "Actions") continue;
        const columnKey = ORDER_TABLE_COL_TO_PMS_COLUMN_KEY[label];
        if (!columnKey) {
          console.warn("saveColumnOrder: no PMS column_key for table column:", label);
          continue;
        }
        columnsPayload.push({ column_key: columnKey, sort_order: sortOrder });
        sortOrder += 1;
      }
      if (columnsPayload.length === 0) return;

      const response = await fetch(`${pmsApiBaseUrl}/api/v1/columns/order`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          broker_flag: pmsBrokerFlag,
          module_name: "order",
          user_id: Number(sharedData?.ID || localStorage.getItem("id") || 0),
          email: userEmail,
          columns: columnsPayload,
        }),
      });
      const result = await response.json();
      console.log("Orders column reorder (PMS):", result);
    } catch (err) {
      console.error("Error saving column order:", err);
    }
  };

  // Drag Start
  const handleDragStart = (e, columnKey) => {
    setDraggedColumn(columnKey);

    const dragPreview = document.createElement("div");
    dragPreview.style.position = "absolute";
    dragPreview.style.top = "-9999px";
    dragPreview.style.padding = "6px 16px";
    dragPreview.style.background = "#C9E0FF";
    dragPreview.style.border = "1px solid #99bde0";
    dragPreview.style.borderRadius = "4px";
    dragPreview.style.color = "#000";
    dragPreview.style.fontSize = "16px";
    dragPreview.style.fontWeight = "bold";
    dragPreview.style.boxShadow = "0 2px 6px rgba(0, 0, 0, 0.15)";
    dragPreview.textContent = columnKey;

    document.body.appendChild(dragPreview);
    e.dataTransfer.setDragImage(dragPreview, 0, 0);

    // Cleanup
    setTimeout(() => {
      document.body.removeChild(dragPreview);
    }, 0);
  };

  // Drag Over
  const handleDragOver = (e) => {
    e.preventDefault(); // Allow drop
  };

  const handleDrop = (e, targetColumnKey) => {
    e.preventDefault();

    const draggedIndex = columnOrder.indexOf(draggedColumn);
    const targetIndex = columnOrder.indexOf(targetColumnKey);

    if (draggedIndex === -1 || targetIndex === -1 || draggedIndex === targetIndex) {
      setDraggedColumn(null);
      return;
    }

    const tableContainer = document.querySelector(".table")?.closest("div");
    if (tableContainer) tableContainer.style.overflowY = "hidden";

    const newColumnOrder = [...columnOrder];
    const [movedColumn] = newColumnOrder.splice(draggedIndex, 1);
    newColumnOrder.splice(targetIndex, 0, movedColumn);

    setColumnOrder(newColumnOrder);
    setDraggedColumn(null);
    saveColumnOrder(newColumnOrder);

    // Animation wrapped in requestAnimationFrame to prevent layout loops
    requestAnimationFrame(() => {
      const cells = document.querySelectorAll(`[data-column-id="${movedColumn}"]`);

      // Start animation
      cells.forEach((cell) => cell.classList.add("column-drop-animate"));

      // Remove animation after timeout
      setTimeout(() => {
        cells.forEach((cell) => cell.classList.remove("column-drop-animate"));

        // Temporary highlight of table
        const table = document.querySelector("table");
        table?.classList.add("table-highlight-column");
        setTimeout(() => {
          table?.classList.remove("table-highlight-column");
          if (tableContainer) tableContainer.style.overflowY = "";
        }, 600);
      }, 850);
    });
  };

  // Drag End
  const handleDragEnd = () => {
    setDraggedColumn(null);
  };

  //----------------------------// End Drag-N-Drop Functions //----------------------------//



  //----------------------------// Pagination Functions //----------------------------//

  // Pagination calculations
  const indexOfLastItem = currentPage * pageSize;
  const indexOfFirstItem = indexOfLastItem - pageSize;
  const currentItems = sortedOrderBook.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(sortedOrderBook.length / pageSize);

  useEffect(() => {
    if (!activeHeaderMenu) return undefined;
    const handleClose = () => setActiveHeaderMenu(null);
    document.addEventListener("mousedown", handleClose);
    window.addEventListener("scroll", handleClose, true);
    window.addEventListener("resize", handleClose);
    return () => {
      document.removeEventListener("mousedown", handleClose);
      window.removeEventListener("scroll", handleClose, true);
      window.removeEventListener("resize", handleClose);
    };
  }, [activeHeaderMenu]);

  const renderHeaderMenu = useCallback((col) => {
    if (col === "Actions") return null;
    const isOpen = activeHeaderMenu?.column === col;
    const selectedDirection = sortConfig?.column === col ? sortConfig?.direction : null;
    const menu = isOpen ? (
      <div style={{ position: "fixed", top: activeHeaderMenu?.top ?? 0, left: activeHeaderMenu?.left ?? 0, width: 184, background: "#ffffff", borderRadius: 12, border: "1px solid #dde3ea", boxShadow: "0 10px 24px rgba(18, 34, 51, 0.18)", padding: "7px", zIndex: 1200 }}>
        <button type="button" onClick={() => { setSortConfig({ column: col, direction: "asc" }); setCurrentPage(1); setActiveHeaderMenu(null); }} style={{ width: "100%", border: "none", background: selectedDirection === "asc" ? "#f4f8ff" : "transparent", textAlign: "left", padding: "7px 9px", borderRadius: 8, color: "#44546a", fontWeight: 500, fontSize: 13, cursor: "pointer" }}><span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}><ArrowUp size={14} /><span>Sort Ascending</span></span></button>
        <button type="button" onClick={() => { setSortConfig({ column: col, direction: "desc" }); setCurrentPage(1); setActiveHeaderMenu(null); }} style={{ width: "100%", border: "none", background: selectedDirection === "desc" ? "#f4f8ff" : "transparent", textAlign: "left", padding: "7px 9px", borderRadius: 8, color: "#44546a", fontWeight: 500, fontSize: 13, cursor: "pointer" }}><span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}><ArrowDown size={14} /><span>Sort Descending</span></span></button>
      </div>
    ) : null;
    return (
      <div style={{ position: "absolute", right: col === "Name" ? 4 : 6, top: "50%", transform: "translateY(-50%)" }}>
        <button type="button" onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); }} onClick={(e) => { e.preventDefault(); e.stopPropagation(); const rect = e.currentTarget.getBoundingClientRect(); const menuWidth = 184; const viewportPadding = 8; const computedLeft = Math.min(Math.max(viewportPadding, rect.right - menuWidth), window.innerWidth - menuWidth - viewportPadding); setActiveHeaderMenu((prev) => prev?.column === col ? null : { column: col, top: rect.bottom + 6, left: computedLeft }); }} style={{ border: "none", background: "transparent", width: 24, height: 24, padding: 0, display: "inline-flex", alignItems: "center", justifyContent: "center", color: "#7f8a99", cursor: "pointer", borderRadius: 6 }}><EllipsisVertical size={14} strokeWidth={2.2} /></button>
        {menu && typeof document !== "undefined" ? createPortal(menu, document.body) : null}
      </div>
    );
  }, [activeHeaderMenu, sortConfig]);

  // Handle page change
  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  // Handle page size change
  const handlePageSizeChange = (event) => {
    setPageSize(Number(event.target.value));
    setCurrentPage(1); // Reset to first page when page size changes
  };

  // Generate page numbers to display
  const getPageNumbers = () => {
    const pageNumbers = [];
    const maxPagesToShow = 3;

    if (totalPages <= maxPagesToShow) {
      for (let i = 1; i <= totalPages; i++) {
        pageNumbers.push(i);
      }
    } else {
      if (currentPage <= 2) {
        for (let i = 1; i <= 3; i++) pageNumbers.push(i);
      } else if (currentPage >= totalPages - 1) {
        for (let i = totalPages - 2; i <= totalPages; i++) pageNumbers.push(i);
      } else {
        for (let i = currentPage - 1; i <= currentPage + 1; i++) pageNumbers.push(i);
      }
    }

    return pageNumbers;
  };



  useEffect(() => {
    setCurrentPage(1);
  }, [filteredOrderBook.length]);


  //----------------------------// End Pagination Functions //----------------------------//




  //----------------------------// Return Main //----------------------------//
  return (
    <>
      {/* <Header /> */}
      <Header tabName={tabName} />
      <div className="wrapper__page pb-5">
        <Sidebar />

        <div className="content_col">
          {/*   <h1>Watchlist</h1> */}

          {/* Watchlist Header with Tabs */}
          <div className="watchlist-header-with-tabs">
            <div className="header-left">
              <h1 className="watchlist-title">{tr("orders")}</h1>

            </div>

            <div
              className="chart-cards-row"
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(5, 1fr)",
                gap: "20px",
                marginTop: "8px",
                marginBottom: "10px",
                width: "100%",
              }}
            >
              <ChartCard
                title={tr("totalOrders")}
                iconSrc={iconTotalOrder}
                value={orderBook.length}
                chartColor="#3B82F6"
                isHovered={false}
                onMouseEnter={() => {}}
                onMouseLeave={() => {}}
                showExpandButton={false}
              />

              <ChartCard
                title={tr("filledOrders")}
                iconSrc={iconFilledOrder}
                value={
                  <span style={{ color: "#1EA355" }}>
                    {orderBook.filter((o) => o.TradedQty > 0).length}
                  </span>
                }
                chartColor="#10B981"
                isHovered={false}
                onMouseEnter={() => {}}
                onMouseLeave={() => {}}
                showExpandButton={false}
              />

              <ChartCard
                title={tr("pendingOrders")}
                iconSrc={iconPendingOrder}
                value={
                  <span style={{ color: "#F59E0B" }}>
                    {orderBook.filter(
                      (o) =>
                        o.PendingQty > 0 &&
                        ["Pending", "Scheduled (AMO)", "ACKED"].includes(
                          String(o.OrderStatus || "").trim(),
                        ),
                    ).length}
                  </span>
                }
                chartColor="#F59E0B"
                isHovered={false}
                onMouseEnter={() => {}}
                onMouseLeave={() => {}}
                showExpandButton={false}
              />

              <ChartCard
                title={tr("rejectedOrders")}
                iconSrc={iconRejectedOrder}
                value={
                  <span style={{ color: "#F44336" }}>
                    {orderBook.filter((o) => {
                      const status = String(o?.OrderStatus || "").toLowerCase();
                      return status.includes("rejected") || status.includes("cancel");
                    }).length}
                  </span>
                }
                chartColor="#EF4444"
                isHovered={false}
                onMouseEnter={() => {}}
                onMouseLeave={() => {}}
                showExpandButton={false}
              />

              <ChartCard
                title={tr("totalVolume")}
                iconSrc={iconGraph}
                value={
                  <span style={{ color: "#111827" }}>
                    {orderBook.reduce((sum, o) => sum + o.TradedQty, 0)}
                  </span>
                }
                chartColor="#8B5CF6"
                isHovered={false}
                onMouseEnter={() => {}}
                onMouseLeave={() => {}}
                showExpandButton={false}
              />
            </div>
          </div>


          {/* Table */}
          <div className="table_main">
            <div className="table_title_with_view_all_row">
              <h5>{tr("ordersOverTime")}</h5>
            </div>
            <div className="table-responsive">

              {isLoading && (
                <Placeholder.Grid
                  rows={20}
                  columns={9}
                  active
                  style={{ paddingLeft: 20, paddingRight: 20, paddingTop: 12 }}
                />
              )}


              {!isLoading && (
                <table className="table db-orders-table">
                  {/* Thead */}
                  <thead>
                    <tr>
                      {columnOrder
                        .filter(col => !["Exch"].includes(col))
                        .map(col => (
                          <th
                            key={col}
                            draggable="true"
                            onDragStart={(e) => handleDragStart(e, col)}
                            onDragOver={(e) => handleDragOver(e)}
                            onDrop={(e) => handleDrop(e, col)}
                            onDragEnd={handleDragEnd}
                            data-column-id={col}
                            style={{
                              position: "relative",
                              textAlign:
                                col === "Name" ? "center" :
                                  col === "Reason" ? "left" :
                                    ["OrderStatus"].includes(col) ? "center" :
                                      "center",
                              minWidth: col === "Name"
                                ? "220px"
                                : ["PQty", "Type", "Quantity"].includes(col)
                                  ? "80px"
                                  : col === "Order Id"
                                    ? "140px"
                                    : "100px",
                              maxWidth: col === "Name" ? "220px" : undefined,
                              width: col === "Name" ? "220px" : undefined,
                              overflow: col === "Name" ? "hidden" : undefined,
                              transition: "all 0.3s ease",
                              transform: draggedColumn === col ? "scale(1.03)" : "scale(1)",
                              boxShadow: draggedColumn === col ? "0 0 10px #C9E0FF" : "none",
                              cursor: "grab",
                              display: ["Net Change", "Quantity", "Actions"].includes(col) ? "none" : undefined,
                              position: col === "Actions" ? "sticky" : undefined,
                              right: col === "Actions" ? 0 : undefined,
                              background: col === "Actions" ? "#fff" : undefined,
                              zIndex: col === "Actions" ? 35 : undefined,
                              paddingRight: col === "Actions" ? 0 : 28,
                            }}
                          >
                            {renderHeaderMenu(col)}
                            {
                              col === "Name" ? (
                                <div className="table_head_search" style={{ width: "220px" }}>
                                  <div style={{ padding: "2px", width: 200 }}>
                                    <button
                                      style={{ padding: "2px 0 2px 5px", borderRight: "1px solid #e4e4e4ff" }}
                                      onClick={() => searchInputRef.current?.focus()}
                                    >
                                      <i className="icon-search" />
                                    </button>
                                    {/* Name */}

                                    <input
                                      type="text"
                                      placeholder={tr("nameSearchHere")}
                                      style={{
                                        paddingLeft: "10px",
                                        marginLeft: "5px",
                                        maxWidth: "150px",
                                        width: "100%",
                                        boxSizing: "border-box",
                                      }}
                                      value={searchQuery}
                                      onChange={(e) => setSearchQuery(e.target.value)}
                                      ref={searchInputRef}
                                    />
                                  </div>
                                </div>
                              ) : col === "Actions" ? (
                                <></>
                              ) : col === "PQty" ? (
                                <Whisper
                                  placement="top"
                                  trigger="hover"
                                  speaker={
                                    <Tooltip className="common__tooltip" style={{ maxWidth: "400px" }}>
                                      {tr("pendingTotalQuantityTitle")}:<br />
                                      - {tr("topLabel")} (P): {tr("pendingQuantity")}<br />
                                      - {tr("bottomLabel")} (Q): {tr("totalOrderQuantity")}
                                    </Tooltip>
                                  }
                                >
                                  <>{tr("pqShort")}</>
                                </Whisper>
                              ) : col === "Order Status" ? (
                                <div >
                                  {translateColumnLabel(col, currentLanguage)}
                                </div>

                              )
                                : col === "Reason" ? (
                                  <div >
                                    {translateColumnLabel(col, currentLanguage)}
                                  </div>

                                )
                                  : col === "Current Price" ? (
                                    <Whisper
                                      placement="top"
                                      trigger="hover"
                                      speaker={
                                        <Tooltip className="common__tooltip" style={{ maxWidth: '400px' }}>
                                          {tr("currentPriceNetChangeTitle")}:<br />
                                          - {tr("topLabel")}: {tr("currentPriceNetChangeTop")}<br />
                                          - {tr("bottomLabel")}: {tr("currentPriceNetChangeBottom")}
                                        </Tooltip>
                                      }
                                    >
                                      <div style={{}}>
                                        {tr("currentPrice")}
                                      </div>
                                    </Whisper>
                                  )
                                    : col === "Net Change" ? (
                                      <div style={{}}></div>
                                    )

                                      : (
                                        translateColumnLabel(col, currentLanguage)
                                      )}
                          </th>
                        ))}
                    </tr>
                  </thead>



                  {/* Tbody */}
                  <tbody>
                    {orderBook.length === 0 ? (
                      <tr>
                        <td colSpan={columnOrder.length} className="table-empty-state-cell">
                          <span className="table-empty-order-text">
                            {tr("noOrderDataAvailable")}
                          </span>
                        </td>
                      </tr>
                    ) : currentItems.length === 0 ? (
                      <tr>
                        <td colSpan={columnOrder.length} className="table-empty-state-cell">
                          {tr("noMatchFound")}
                        </td>
                      </tr>
                    ) : (
                      currentItems.map((order, index) => (
                        <tr key={order.Symbol || index} >
                          {columnOrder.map((col) => {
                            let content;

                            if (col === "Name") {
                              const symbolName = String(
                                order.ScripName ||
                                  order.Symbol ||
                                  order.symbol ||
                                  order.TradingSymbol ||
                                  "",
                              ).trim();
                              const exchRaw = String(
                                order?.Exch || order?.exch || order?.exchange || "",
                              ).toUpperCase();
                              const exchTypeRaw = String(
                                order?.ExchType || order?.exchType || "C",
                              ).toUpperCase();
                              const exchShort = exchRaw.startsWith("N")
                                ? "NSE"
                                : exchRaw.startsWith("B")
                                  ? "BSE"
                                  : exchRaw.startsWith("M")
                                    ? "MCX"
                                    : "BSE";
                              const exchSegment = exchTypeRaw.startsWith("D")
                                ? "Derivative"
                                : "Cash";
                              const exchFullLabel = `${exchShort} ${exchSegment}`;

                              content = (
                                <div
                                  style={{
                                    paddingLeft: "10px",
                                    width: "200px",
                                    minWidth: "200px",
                                    maxWidth: "200px",
                                    boxSizing: "border-box",
                                  }}
                                >
                                  <div
                                    className="main_table_company_cell"
                                    style={{ minWidth: 0 }}
                                  >
                                    <Whisper
                                      placement="top"
                                      trigger="hover"
                                      speaker={
                                        <Tooltip className="common__tooltip">
                                          {order.FullName || order.ScripName}
                                        </Tooltip>
                                      }
                                    >
                                      <h5
                                        className="uppercase"
                                        style={{
                                          maxWidth: 145,
                                          overflow: "hidden",
                                          textOverflow: "ellipsis",
                                          whiteSpace: "nowrap",
                                          margin: 0,
                                        }}
                                      >
                                        {order.FullName || order.ScripName}
                                      </h5>
                                    </Whisper>
                                  </div>
                                  <ul
                                    className="company_table_tags"
                                    style={{
                                      paddingLeft: 0,
                                      marginTop: 4,
                                      flexWrap: "nowrap",
                                      alignItems: "center",
                                      marginBottom: 0,
                                      gap: "4px",
                                      minWidth: 0,
                                    }}
                                  >
                                    <li
                                      className="csymbol"
                                      style={{
                                        minWidth: 0,
                                        maxWidth: 85,
                                        overflow: "hidden",
                                      }}
                                    >
                                      <Whisper
                                        placement="top"
                                        trigger="hover"
                                        speaker={
                                          <Tooltip
                                            className="common__tooltip"
                                            style={{ maxWidth: "320px" }}
                                          >
                                            {symbolName || "-"}
                                          </Tooltip>
                                        }
                                      >
                                        <span
                                          style={{
                                            display: "inline-block",
                                            maxWidth: "100%",
                                            overflow: "hidden",
                                            textOverflow: "ellipsis",
                                            whiteSpace: "nowrap",
                                            verticalAlign: "bottom",
                                            cursor: "default",
                                          }}
                                        >
                                          {symbolName || "-"}
                                        </span>
                                      </Whisper>
                                    </li>
                                    <li
                                      className="cbse_nse"
                                      style={{
                                        minWidth: 0,
                                        flexShrink: 0,
                                      }}
                                    >
                                      <Whisper
                                        placement="top"
                                        trigger="hover"
                                        speaker={
                                          <Tooltip className="common__tooltip">
                                            {exchFullLabel}
                                          </Tooltip>
                                        }
                                      >
                                        <span
                                          style={{
                                            cursor: "default",
                                            whiteSpace: "nowrap",
                                          }}
                                        >
                                          {exchShort}
                                        </span>
                                      </Whisper>
                                    </li>
                                  </ul>
                                </div>
                              );
                            }

                            else if (col === "Current Price") {
                              const scripCode = getOrderLivePriceCacheKey(order);
                              const livePrice = livePriceCache.current[scripCode];
                              const pClose = livePCloseCache.current[scripCode];
                              const stocksData = order?.stocks_data || {};
                              const ohlc = stocksData?.ohlc || {};
                              const currentPrice = Number(
                                livePrice ??
                                stocksData?.close ??
                                stocksData?.last_price ??
                                stocksData?.LastRate ??
                                ohlc?.close ??
                                stocksData?.open ??
                                ohlc?.open ??
                                order.CurrentPrice ??
                                0,
                              );
                              const previousClose = Number(
                                pClose ??
                                stocksData?.PClose ??
                                stocksData?.previous_close ??
                                stocksData?.pc ??
                                stocksData?.open ??
                                ohlc?.open ??
                                order.PreviousClose ??
                                0,
                              );
                              const hasBoth =
                                Number.isFinite(currentPrice) &&
                                Number.isFinite(previousClose) &&
                                previousClose !== 0;
                              const isPositive = hasBoth
                                ? currentPrice >= previousClose
                                : true;

                              // Get Net Change data
                              const priceCurrentPrice = currentPrice;
                              const pricePreviousClose = previousClose;
                              const hasChangeData = hasBoth;
                              const priceDifference = hasChangeData
                                ? priceCurrentPrice - pricePreviousClose
                                : 0;
                              const changePercent = hasChangeData
                                ? ((priceDifference / pricePreviousClose) * 100).toFixed(2)
                                : "0.00";
                              const isChangePositive = hasChangeData
                                ? priceDifference >= 0
                                : isPositive;

                              content = (
                                <div className="trade__price__cell" style={{ textAlign: "center", minWidth: "140px" }}>
                                  {/* Current Price (Top) */}
                                  <div className={isPositive ? "text_success_color" : "text_danger_color"}>
                                    ₹{formatNumber(currentPrice)}
                                    <i className={`icon-arrow-${isPositive ? 'up' : 'down'}`} style={{ paddingLeft: "4px" }}></i>
                                  </div>

                                  {/* Separator Line */}
                                  <div style={{
                                    width: "30px",
                                    height: "1px",
                                    background: "linear-gradient(90deg, transparent, #cbd5e1, transparent)",
                                    margin: "4px auto",
                                    opacity: "0.5"
                                  }}></div>

                                  {/* Net Change (Bottom) */}
                                  <div className={hasChangeData ? (isChangePositive ? "text_success_color" : "text_danger_color") : "text_muted"} style={{}}>
                                    {hasChangeData ? (
                                      <>
                                        {isChangePositive ? '+' : ''}₹{Math.abs(priceDifference).toFixed(2)}
                                        <span style={{ paddingLeft: "4px" }}>
                                          ({isChangePositive ? '+' : ''}{changePercent}%)
                                        </span>
                                        <i className={`icon-arrow-${isChangePositive ? 'up' : 'down'}`} style={{ marginLeft: "4px" }}></i>
                                      </>
                                    ) : (
                                      <>
                                        ₹0.00 (0.00%)
                                        <i className="icon-arrow-down" style={{ marginLeft: "4px" }} />
                                      </>
                                    )}
                                  </div>
                                </div>
                              );
                            }

                            else if (col === "Net Change") {
                              content = <></>;
                            }


                            else if (col === "Exch") {
                              content = (
                                <>
                                </>
                              );
                            }

                            else if (col === "Net Change") {
                              content = (
                                <>
                                </>
                              );
                            }



                            else if (["Order Id", "PQty", "Type", "Quantity", "Date", "Order Status", "Reason"].includes(col)) {
                              if (col === "Order Id")
                                content = (
                                  <Whisper
                                    placement="top"
                                    trigger="hover"
                                    speaker={
                                      <Tooltip className="common__tooltip">
                                        {String(order.BrokerOrderId ?? "-")}
                                      </Tooltip>
                                    }
                                  >
                                    <div
                                      style={{
                                        width: "140px",
                                        minWidth: "140px",
                                        maxWidth: "140px",
                                        margin: "0 auto",
                                        overflow: "hidden",
                                        textAlign: "center",
                                        cursor: "pointer",
                                      }}
                                    >
                                      {activeBrokerFlag === "grow" && (
                                        <div style={{ width: "130px", margin: "0 auto" }}>
                                          <div
                                            style={{
                                              display: "flex",
                                              alignItems: "center",
                                              justifyContent: "center",
                                              fontSize: "12px",
                                              gap: "6px",
                                              marginBottom: "4px",
                                            }}
                                          >
                                            {(() => {
                                              const orderType = String(
                                                order?.order_type ?? order?.OrderType ?? "-",
                                              ).toUpperCase();
                                              const isMarket = orderType === "MARKET";
                                              return (
                                                <span style={{ color: "#111827", fontWeight: 500 }}>
                                                  {formatNumber(order?.price ?? order?.Price ?? 0)}{" "}
                                                  <span style={{ color: "#94A3B8" }}>-</span>{" "}
                                                  <span
                                                    style={{
                                                      fontWeight: 600,
                                                      color: isMarket ? "#2563EB" : "#B45309",
                                                    }}
                                                  >
                                                    {orderType}
                                                  </span>
                                                </span>
                                              );
                                            })()}
                                          </div>
                                          <div
                                            style={{
                                              width: "100%",
                                              height: "1px",
                                              background:
                                                "linear-gradient(90deg, transparent, #cbd5e1, transparent)",
                                              margin: "4px 0",
                                              opacity: "0.6",
                                            }}
                                          ></div>
                                          <div
                                            style={{
                                              textAlign: "center",
                                              overflow: "hidden",
                                              textOverflow: "ellipsis",
                                              whiteSpace: "nowrap",
                                              fontSize: "12px",
                                              color: "#475569",
                                            }}
                                          >
                                            {order.BrokerOrderId ?? "-"}
                                          </div>
                                        </div>
                                      )}
                                      {activeBrokerFlag !== "grow" && (
                                        <div style={{ width: "130px", margin: "0 auto" }}>
                                          <div
                                            style={{
                                              display: "flex",
                                              alignItems: "center",
                                              justifyContent: "center",
                                              fontSize: "12px",
                                              gap: "6px",
                                              marginBottom: "4px",
                                            }}
                                          >
                                            {(() => {
                                              const isMarket = String(order?.AtMarket || "")
                                                .toUpperCase()
                                                .trim() === "Y";
                                              const orderType = isMarket ? "MARKET" : "LIMIT";
                                              return (
                                                <span style={{ color: "#111827", fontWeight: 500 }}>
                                                  {formatNumber(
                                                    order?.AveragePrice ??
                                                    order?.Rate ??
                                                    order?.Price ??
                                                    0,
                                                  )}{" "}
                                                  <span style={{ color: "#94A3B8" }}>-</span>{" "}
                                                  <span
                                                    style={{
                                                      fontWeight: 600,
                                                      color: isMarket ? "#2563EB" : "#B45309",
                                                    }}
                                                  >
                                                    {orderType}
                                                  </span>
                                                </span>
                                              );
                                            })()}
                                          </div>
                                          <div
                                            style={{
                                              width: "100%",
                                              height: "1px",
                                              background:
                                                "linear-gradient(90deg, transparent, #cbd5e1, transparent)",
                                              margin: "4px 0",
                                              opacity: "0.6",
                                            }}
                                          ></div>
                                          <div
                                            style={{
                                              textAlign: "center",
                                              overflow: "hidden",
                                              textOverflow: "ellipsis",
                                              whiteSpace: "nowrap",
                                              fontSize: "12px",
                                              color: "#475569",
                                            }}
                                          >
                                            {order.BrokerOrderId ?? "-"}
                                          </div>
                                        </div>
                                      )}
                                    </div>
                                  </Whisper>
                                );
                              else if (col === "PQty") {
                                content = (
                                  <div className="trade__price__cell" style={{ textAlign: "center", width: "80px", margin: "auto" }}>
                                    {/* Pending Qty (Top) */}
                                    <div style={{ display: "grid", gridTemplateColumns: "16px 1fr", alignItems: "center", justifyItems: "center", gap: "4px" }}>
                                      <span style={{ fontSize: "0.7em", opacity: "0.6", color: "#64748B", fontWeight: "600" }}>P</span>
                                      <div style={{ textAlign: "center" }}>
                                        {order.PendingQty ?? "-"}
                                      </div>
                                    </div>

                                    {/* Separator Line */}
                                    <div style={{
                                      width: "25px",
                                      height: "1px",
                                      background: "linear-gradient(90deg, transparent, #cbd5e1, transparent)",
                                      margin: "4px auto",
                                      opacity: "0.5"
                                    }}></div>

                                    {/* Total Qty (Bottom) */}
                                    <div style={{ display: "grid", gridTemplateColumns: "16px 1fr", alignItems: "center", justifyItems: "center", gap: "4px" }}>
                                      <span style={{ fontSize: "0.7em", opacity: "0.6", color: "#64748B", fontWeight: "600" }}>Q</span>
                                      <div style={{ textAlign: "center" }}>
                                        {order.Qty ?? "-"}
                                      </div>
                                    </div>
                                  </div>
                                );
                              }
                              else if (col === "Type") {
                                const typeLabel = order.BuySell === "B" ? "BUY" : "SELL";
                                content = (
                                  <span
                                    style={{
                                      color: typeLabel === "BUY" ? "#24AB81" : "#D25656",
                                      fontWeight: 600,
                                    }}
                                  >
                                    {typeLabel}
                                  </span>
                                );
                              }
                              else if (col === "Quantity") content = <></>;
                              else if (col === "Date") {
                                content = (
                                  <>
                                    {convertDate(order.BrokerOrderTime)}<br />
                                    {convertDate1(order.BrokerOrderTime)}
                                  </>
                                );
                              }
                              else if (col === "Order Status") {
                                content = (
                                  <>
                                    <div
                                      className=""
                                      style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}
                                    >
                                      <span className={getStatusClass(order.OrderStatus) + ' status_box'}>
                                        <span className="status_circle"></span>
                                        {order.OrderStatus}
                                      </span>
                                    </div>
                                  </>
                                );
                              }
                              else if (col === "Reason") {
                                content = (
                                  <Whisper
                                    placement="top"
                                    trigger="hover"
                                    speaker={
                                      <Tooltip className="common__tooltip">
                                        {String(getReason(order) || "-")}
                                      </Tooltip>
                                    }
                                  >
                                    <div
                                      className="reason-cell"
                                      style={{
                                        color: "green",
                                        width: "180px",
                                        minWidth: "180px",
                                        maxWidth: "180px",
                                        whiteSpace: "normal",
                                        overflowWrap: "anywhere",
                                        wordBreak: "break-word",
                                        cursor: "pointer",
                                      }}
                                    // title={String(getReason(order) || "-")}
                                    >
                                      <span>{getReason(order)}</span>
                                    </div>
                                  </Whisper>
                                );
                              }
                            }

                            else if (col === "Actions") {
                              content = (
                                <ul className="table__action_hover orders-action-hover" style={{ top: "0" }}>
                                  <li>
                                    <div className="buy-sell-segmented">
                                      <button
                                        className="btn_buy wl-premium-action-btn wl-premium-buy"
                                        data-bs-toggle="modal"
                                        data-bs-target="#BuySellModal"
                                        onClick={() => {
                                          SettingBuyModalDetails(order, "Buy");
                                        }}
                                      >
                                        {tr("buy")}
                                      </button>
                                      <button
                                        className="btn_sell wl-premium-action-btn wl-premium-sell"
                                        data-bs-toggle="modal"
                                        data-bs-target="#BuySellModal"
                                        onClick={() => {
                                          SettingBuyModalDetails(order, "Sell");
                                        }}
                                      >
                                        {tr("sell")}
                                      </button>
                                    </div>
                                  </li>
                                  {isOrderActionable(order) && (
                                    <li>
                                      <button
                                        className="wl-premium-action-btn wl-premium-order-modify"
                                        data-bs-toggle="modal"
                                        data-bs-target="#ModifyOrderModal"
                                        onClick={() => openModifyOrderModal(order)}
                                      >
                                        Modify
                                      </button>
                                    </li>
                                  )}
                                  {isOrderActionable(order) && (
                                    <li>
                                      <button
                                        className="wl-premium-action-btn wl-premium-order-cancel"
                                        data-bs-toggle="modal"
                                        data-bs-target="#CancelOrderConfirmModal"
                                        onClick={() => openCancelOrderModal(order)}
                                      >
                                        Cancel
                                      </button>
                                    </li>
                                  )}
                                  {activeBrokerFlag !== "grow" && (
                                    <Whisper placement="top" trigger="hover" speaker={<Tooltip className="common__tooltip">{tr("marketDepth")}</Tooltip>}>
                                      <li>
                                        <button
                                          ref={marketDepthButtonRef}
                                          onClick={(e) => handleMarketDepthClickOrder(order, e)}
                                          className="btn_trading market-depth-trigger"
                                        >
                                          <MarketDepthIcon />
                                        </button>
                                      </li>
                                    </Whisper>
                                  )}

                                </ul>
                              );
                            }
                            else {
                              content = order[col] ?? "-";
                              content = (
                                <div style={{
                                  textAlign: ["ProfitPct", "Volume"].includes(col) ? "center" : "center"
                                }}>
                                  {content}
                                </div>
                              );
                            }

                            return (
                              <td
                                key={col}
                                className={col === "Actions" ? "action-cell" : undefined}
                                style={{
                                  textAlign: col === "Name" || col === "Reason" ? "left" : "center",
                                  paddingLeft: col === "Actions" ? 0 : undefined,
                                  paddingRight: col === "Actions" ? 0 : undefined,
                                  padding: col === "Name" ? '10px' : undefined,
                                  display: ["Net Change", "Quantity"].includes(col) ? "none" : undefined,
                                  position: col === "Actions" ? "sticky" : undefined,
                                  right: col === "Actions" ? 0 : undefined,
                                  background: col === "Actions" ? "#fff" : undefined,
                                  zIndex: col === "Actions" ? 35 : undefined,
                                }}
                              >
                                {content}
                              </td>
                            );
                          })}
                        </tr>
                      ))
                    )}
                  </tbody>



                </table>
              )}




            </div>

            {/* {!isLoading && ( */}
            {!isLoading && orderBook.length > 0 && (
              <div className="table__footer_row">
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <h6>
                    {indexOfFirstItem + 1} {tr("to")} {Math.min(indexOfLastItem, filteredOrderBook.length)} {tr("of")} {filteredOrderBook.length} {tr("items")}
                  </h6>
                  <TablePageSizeSelect
                    value={pageSize}
                    onChange={handlePageSizeChange}
                  />
                </div>

                <nav aria-label="Page navigation example">
                  <ul className="pagination justify-content-center">
                    {/* First Page Button */}
                    <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                      <a
                        className="page-link"
                        onClick={() => currentPage > 1 && handlePageChange(1)}
                        style={{ cursor: currentPage === 1 ? 'not-allowed' : 'pointer' }}
                        title={tr("firstPage")}
                      >
                        <MdKeyboardDoubleArrowLeft style={{ fontSize: "18px" }} />
                      </a>
                    </li>

                    {/* Previous Page Button */}
                    <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                      <a
                        className="page-link"
                        onClick={() => currentPage > 1 && handlePageChange(currentPage - 1)}
                        style={{ cursor: currentPage === 1 ? 'not-allowed' : 'pointer' }}
                        title={tr("previousPage")}
                      >
                        <IoIosArrowBack />
                      </a>
                    </li>

                    {/* Page Numbers */}
                    {getPageNumbers().map((pageNum) => (
                      <li
                        key={pageNum}
                        className={`page-item ${currentPage === pageNum ? 'active' : ''}`}
                      >
                        <a
                          className="page-link"
                          onClick={() => handlePageChange(pageNum)}
                          style={{ cursor: 'pointer' }}
                        >
                          {pageNum}
                        </a>
                      </li>
                    ))}

                    {/* Next Page Button */}
                    <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                      <a
                        className="page-link"
                        onClick={() => currentPage < totalPages && handlePageChange(currentPage + 1)}
                        style={{ cursor: currentPage === totalPages ? 'not-allowed' : 'pointer' }}
                        title={tr("nextPage")}
                      >
                        <IoIosArrowForward />
                      </a>
                    </li>

                    {/* Last Page Button */}
                    <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                      <a
                        className="page-link"
                        onClick={() => currentPage < totalPages && handlePageChange(totalPages)}
                        style={{ cursor: currentPage === totalPages ? 'not-allowed' : 'pointer' }}
                        title={tr("lastPage")}
                      >
                        <MdKeyboardDoubleArrowRight style={{ fontSize: "18px" }} />
                      </a>
                    </li>
                  </ul>
                </nav>
              </div>
            )}



          </div>
        </div>
      </div>



      {/* Buy Sell Modal */}
      <div className="modal fade modal_md modern-trading-modal" id="BuySellModal" aria-labelledby="BuySellModalLabel" aria-hidden="true">
        <div className="modal-dialog modal-dialog-centered modal-dialog-modern">
          <div className="modal-content modal-content-modern">
            {/* Modal Header */}
            <div className="modal-header modal-header-modern">
              <div className="modal-header-content">
                <div className="modal-title-wrapper">
                  <div className={`trading-type-badge ${buySellModalDetails.Type.toLowerCase()}`}>
                    <img
                      src={buySellModalDetails.Type === "Buy" ? ArrowTrendUp : ArrowTrendDown}
                      className="trading-icon"
                      alt="icon"
                    />
                    <span className="trading-type-text">{buySellModalDetails.Type}</span>
                  </div>
                  <h3 className="modal-title-modern">
                    {buySellModalDetails.Symbol}
                  </h3>
                </div>
                <button
                  type="button"
                  className="btn-close-modern"
                  data-bs-dismiss="modal"
                  aria-label={tr("close")}
                >
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <line x1="18" y1="6" x2="6" y2="18"></line>
                    <line x1="6" y1="6" x2="18" y2="18"></line>
                  </svg>
                </button>
              </div>
            </div>

            <div className="modal-body modal-body-modern">
              <AnimatePresence mode="wait">
                {/* Screen 1 */}
                {!isPreview && (
                  <motion.div
                    key="screen1"
                    initial={{ y: 100, opacity: 0 }}
                    animate={{ y: 0, opacity: 1 }}
                    exit={{ y: -100, opacity: 0 }}
                    transition={{ duration: 0.4, ease: "easeInOut" }}
                  >
                    <div className="sell__content"
                      style={{
                        backgroundColor: buySellModalDetails.Type === "Buy" ? "#F6FCF9" : "#fff7f7",
                        border: `1px solid ${buySellModalDetails.Type === "Buy" ? "#B5E8CF" : "#F5B5B5"}`
                      }}
                    >
                      <div className="sell__content_inner d-flex justify-content-between align-items-center">
                        <div>
                          <h5 className="mb-0">{buySellModalDetails.Symbol}</h5>
                          <h6 className="mb-0">{buySellModalDetails.FullName}</h6>
                        </div>
                        {/* LivePrice/ Current Price */}
                        <p className="mb-0">
                          {(() => {
                            const cacheKey = getOrderLivePriceCacheKey(
                              buySellModalDetails.order,
                            );

                            const livePrice = livePriceCache.current[cacheKey];
                            const pClose = livePCloseCache.current[cacheKey];
                            const changePercent = liveChangePercentageCache.current[cacheKey];
                            const change = liveDiffCache.current[cacheKey];

                            const currentPrice = livePrice || buySellModalDetails.order?.Rate || 0;
                            const previousClose = pClose || buySellModalDetails.order?.Rate || 0;
                            const actualChangePercent = changePercent || 0;
                            const actualChange = change || 0;

                            const isPositive = parseFloat(actualChange) >= 0;

                            return (
                              <>
                                <span className={`trade__price__cell ${isPositive ? "text_success_color" : "text_danger_color"
                                  }`}>
                                  ₹{formatNumber(currentPrice)}
                                </span>
                                <br />
                                <span className={isPositive ? "text-success" : "text-danger"}>
                                  ({isPositive ? '+' : ''}{actualChangePercent ? actualChangePercent.toFixed(2) : '0.00'}%)
                                  <i className={`icon-arrow-${isPositive ? 'up' : 'down'} ms-1`} style={{ fontSize: '0.8em' }}></i>
                                </span>
                                {livePrice && (
                                  <br />
                                )}
                              </>
                            );
                          })()}
                        </p>


                      </div>
                    </div>

                    <div className="row mt-3">
                      <div className="col-md-6">
                        <label className="form-label" style={{ fontSize: '12px', fontWeight: '500', color: '#64748b', marginBottom: '6px' }}>{tr("orderType")}</label>
                        <SelectPicker
                          data={OrderType}
                          searchable={false}
                          container={document.body}
                          className="custom-select-picker"
                          value={orderType}
                          onChange={setOrderType}
                        />
                      </div>

                      {/* <div className="col-md-6">
                                    <label className="form-label">{tr("qty")}</label>
                                    <input
                                      type="number"
                                      className="form-control"
                                      value={qty}
                                      onChange={(e) => setQty(Number(e.target.value))}
                                    />
                                  </div> */}
                      <div className="col-md-6">
                        <label className="form-label" style={{ fontSize: '12px', fontWeight: '500', color: '#64748b', marginBottom: '6px' }}>{tr("qty")}</label>
                        <input
                          type="number"
                          className="form-control"
                          value={buySellModalDetails.OrderQty}
                          onChange={(e) => setBuySellModalDetails(prev => ({
                            ...prev,
                            OrderQty: Number(e.target.value)
                          }))}
                          style={{
                            width: "100%",
                            height: "44px",
                            borderRadius: "10px",
                            border: "1px solid #d5dfed",
                            padding: "10px 12px",
                            fontSize: "14px",
                            color: "#0f172a",
                            outline: "none",
                            boxShadow: "none"
                          }}
                        />
                      </div>

                    </div>

                    {/* {orderType === "Limit Order" && (
                                  <div className="mt-3">
                                    <label className="form-label">{tr("limitPrice")}</label>
                                    <input type="number" className="form-control" />
                                  </div>
                                )} */}
                    {orderType === "Limit Order" && (
                      <div className="mt-3">
                        <label className="form-label" style={{ fontSize: '12px', fontWeight: '500', color: '#64748b', marginBottom: '6px' }}>{tr("limitPrice")}</label>
                        <input
                          type="number"
                          className="form-control"
                          value={buySellModalDetails.LimitPrice}
                          onChange={(e) => setBuySellModalDetails(prev => ({
                            ...prev,
                            LimitPrice: Number(e.target.value)
                          }))}
                          style={{
                            width: "100%",
                            height: "44px",
                            borderRadius: "10px",
                            border: "1px solid #d5dfed",
                            padding: "10px 12px",
                            fontSize: "14px",
                            color: "#0f172a",
                            outline: "none",
                            boxShadow: "none"
                          }}
                        />
                      </div>
                    )}



                    {buySellModalDetails.OrderQty > 0 && (
                      <motion.div
                        className="order-summary mt-4 p-3 border roundedX"
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: "auto" }}
                        exit={{ opacity: 0, height: 0 }}
                        transition={{ duration: 0.4 }}
                        style={{
                          backgroundColor: buySellModalDetails.Type === "Buy" ? "#F6FCF9" : "#fff7f7",
                          border: `1px solid ${buySellModalDetails.Type === "Buy" ? "#B5E8CF" : "#F5B5B5"}`
                        }}
                      >
                        <h6 className="fw-bold mb-3">{tr("orderSummary")}</h6>
                        <div className="order-row d-flex justify-content-between">
                          <span className="label">{tr("orderType")}</span>
                          <span className="value">{orderType}</span>
                        </div>
                        <div className="order-row d-flex justify-content-between">
                          <span className="label">{tr("quantity")}</span>
                          <span className="value">{buySellModalDetails.OrderQty} {tr("shares")}</span>
                        </div>
                        <div className="order-row d-flex justify-content-between">
                          <span className="label">{tr("pricePerShare")}</span>
                          <span className="value">₹{formatNumber(getPriceForCalculation())}</span>
                        </div>
                        <div className="order-row d-flex justify-content-between">
                          <span className="label">{tr("subtotal")}</span>
                          <span className="value">₹{formatNumber(buySellModalDetails.OrderQty * getPriceForCalculation())}</span>
                        </div>
                        <div className="order-row d-flex justify-content-between">
                          <span className="label">{tr("estFees")}</span>
                          <span className="value">₹0.00</span>
                        </div>
                        <hr />
                        <div className="order-total d-flex justify-content-between fw-bold">
                          <span>{tr("totalAmount")}</span>
                          <span>₹{formatNumber(buySellModalDetails.OrderQty * getPriceForCalculation() + 0.00)}</span>
                        </div>
                      </motion.div>
                    )}


                  </motion.div>
                )}


                {/* Screen 2 */}
                {isPreview && (
                  <motion.div
                    key="screen2"
                    initial={{ y: 100, opacity: 0 }}
                    animate={{ y: 0, opacity: 1 }}
                    exit={{ y: -100, opacity: 0 }}
                    transition={{ duration: 0.4, ease: "easeInOut" }}
                  >
                    <div className="text-center mb-3">
                      <div className="preview_order_icon preview_buy_order_icon"
                        style={{ backgroundColor: buySellModalDetails.Type === "Buy" ? "#E1FBE9" : "#fff7f7" }}
                      >
                        {/* <img src={ArrowTrendDown} alt="icon" /> */}
                        <img
                          src={buySellModalDetails.Type === "Buy" ? ArrowTrendUp : ArrowTrendDown}
                          alt="icon"
                        />
                      </div>
                      <h5>{tr("confirmOrder")}</h5>
                      <p>{tr("pleaseReviewOrderDetails")}</p>
                    </div>

                    <div className="order-summary-content">
                      <div className="order-row">
                        <span className="label">{tr("orderType")}</span>
                        <span className="value">{orderType}</span>
                      </div>

                      <div className="order-row">
                        <span className="label">{tr("quantity")}</span>
                        <span className="value">{buySellModalDetails.OrderQty} {tr("shares")}</span>
                      </div>

                      <div className="order-row">
                        <span className="label">{tr("pricePerShare")}</span>
                        <span className="value">₹{formatNumber(getPriceForCalculation())}</span>
                      </div>

                      <div className="order-row">
                        <span className="label">{tr("subtotal")}</span>
                        <span className="value capitalize">
                          ₹{formatNumber(buySellModalDetails.OrderQty * getPriceForCalculation())}
                        </span>
                      </div>

                      <div className="order-row">
                        <span className="label">{tr("estFees")}</span>
                        <span className="value capitalize">₹0.00</span>
                      </div>

                      <div className="divider"></div>

                      <div className="order-total">
                        <span className="total-label">{tr("totalAmount")}</span>
                        <span className="total-value">
                          ₹{formatNumber(buySellModalDetails.OrderQty * getPriceForCalculation() + 0.00)}
                        </span>
                      </div>
                    </div>
                  </motion.div>
                )}

              </AnimatePresence>
            </div>

            <div className="modal-footer modal__footer_btn" style={{ width: "100%" }}>
              {!isPreview ? (
                <div style={{ width: "100%", display: "flex", gap: "0.75rem" }} >
                  <button type="button" className="btn-modern btn-secondary-modern" data-bs-dismiss="modal">
                    {tr("cancel")}
                  </button>

                  <button
                    type="button"
                    className="btn-modern"
                    onClick={handlePreview}>
                    {tr("previewOrder")}
                  </button>
                </div>
              ) : (
                <>



                  <div style={{ width: "100%", display: "flex", gap: "0.75rem" }} >

                    <button type="button" className="btn-modern btn-secondary-modern"
                      onClick={handleGoBack}
                    >
                      {tr("goBack")}
                    </button>

                    <button
                      type="button"
                      className={`btn-modern btn-success-modern ${buySellModalDetails.Type === 'Buy' ? 'buy' : 'sell'}`}
                      onClick={(e) => {
                        fetchDataPortfolio(buySellModalDetails.order, buySellModalDetails.Type, "Portfolio");
                        e.target.disabled = true;
                        e.target.textContent = tr("processing");

                        setTimeout(() => {
                          setIsPreview(false); // or handleGoBack()
                          e.target.disabled = false;
                          e.target.textContent = tr("confirmOrder");
                        }, 1000);

                      }}

                      data-bs-dismiss="modal"
                    >
                      {tr("confirmOrder")}
                    </button>
                  </div>


                </>
              )}
            </div>
          </div>
        </div>
      </div>





      {/* Modify Order Modal */}
      <button
        id="closeModifyOrderModalBtn"
        type="button"
        className="d-none"
        data-bs-dismiss="modal"
        data-bs-target="#ModifyOrderModal"
      />
      <div
        className="modal fade modal_md"
        id="ModifyOrderModal"
        aria-labelledby="ModifyOrderModalLabel"
        aria-hidden="true"
      >
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content">
            <div
              className="modal-header"
              style={{
                backgroundColor: "#F5F9FF",
                border: "1px solid #D6E4FF",
              }}
            >
              <div>
                <h3 className="mb-0">
                  Modify {modifyOrderModalDetails.Symbol || "Order"}
                </h3>
                <h6 className="mb-0" style={{ color: "#64748B" }}>
                  {modifyOrderModalDetails.Symbol || "-"}{" "}
                  {modifyOrderModalDetails.FullName
                    ? `- ${modifyOrderModalDetails.FullName}`
                    : ""}
                </h6>
              </div>
              <button
                type="button"
                className="btn-close"
                data-bs-dismiss="modal"
                aria-label={tr("close")}
              ></button>
            </div>
            <div className="modal-body" style={{ backgroundColor: "#F8FBFF" }}>
              <div
                className="sell__content mb-3"
                style={{
                  backgroundColor: "#F5F9FF",
                  border: "1px solid #D6E4FF",
                }}
              >
                <div className="sell__content_inner d-flex justify-content-between align-items-center">
                  <div>
                    <h5 className="mb-0">{modifyOrderModalDetails.Symbol || "-"}</h5>
                    <h6 className="mb-0">
                      {modifyOrderModalDetails.FullName || "Modify selected order details"}
                    </h6>
                  </div>
                  <div style={{ textAlign: "right" }}>
                    <p className="mb-0" style={{ fontSize: "12px", color: "#64748B" }}>
                      {activeBrokerFlag === "grow"
                        ? "Groww Order ID"
                        : isAngelOneBroker
                          ? "AngelOne Order ID"
                          : "Exchange Order ID"}
                    </p>
                    <p className="mb-0" style={{ fontWeight: 600, fontSize: "12px", color: "#1E293B" }}>
                      {activeBrokerFlag === "grow"
                        ? modifyOrderModalDetails.growwOrderId || "-"
                        : isAngelOneBroker
                          ? modifyOrderModalDetails.angelOrderId || "-"
                          : modifyOrderModalDetails.exchOrderID || "-"}
                    </p>
                  </div>
                </div>
              </div>
              <div className="row g-3">
                <div className="col-md-6">
                  <label className="form-label">Quantity</label>
                  <input
                    type="number"
                    min="1"
                    className="form-control"
                    value={modifyOrderModalDetails.quantity}
                    disabled={activeBrokerFlag === "5paisa"}
                    onChange={(e) =>
                      setModifyOrderModalDetails((prev) => ({
                        ...prev,
                        quantity: Number(e.target.value),
                      }))
                    }
                  />
                  {activeBrokerFlag === "5paisa" && (
                    <small style={{ color: "#64748B" }}>
                      Quantity update is not supported for 5paisa modify API.
                    </small>
                  )}
                </div>
                <div className="col-md-6">
                  <label className="form-label">Price</label>
                  <input
                    type="number"
                    min="0"
                    className="form-control"
                    value={modifyOrderModalDetails.price}
                    disabled={modifyOrderModalDetails.orderType === "MARKET"}
                    onChange={(e) =>
                      setModifyOrderModalDetails((prev) => ({
                        ...prev,
                        price: Number(e.target.value),
                      }))
                    }
                  />
                  {modifyOrderModalDetails.orderType === "MARKET" && (
                    <small style={{ color: "#64748B" }}>
                      Price is set to 0 for market orders.
                    </small>
                  )}
                </div>
                {isAngelOneBroker && (
                  <div className="col-md-6">
                    <label className="form-label">Order Type</label>
                    <SelectPicker
                      data={modifyOrderTypeOptions}
                      searchable={false}
                      container={document.body}
                      className="custom-select-picker"
                      value={modifyOrderModalDetails.orderType}
                      onChange={(value) =>
                        setModifyOrderModalDetails((prev) => ({
                          ...prev,
                          orderType: String(value || "LIMIT").toUpperCase(),
                          price: String(value || "").toUpperCase() === "MARKET" ? 0 : prev.price,
                        }))
                      }
                    />
                  </div>
                )}
                {!isAngelOneBroker && (
                  <div className="col-md-6">
                    <label className="form-label">
                      {activeBrokerFlag === "grow" ? "Trigger Price" : "Stop Loss Price"}
                    </label>
                    <input
                      type="number"
                      min="0"
                      className="form-control"
                      value={modifyOrderModalDetails.triggerPrice}
                      onChange={(e) =>
                        setModifyOrderModalDetails((prev) => ({
                          ...prev,
                          triggerPrice: Number(e.target.value),
                        }))
                      }
                    />
                  </div>
                )}
                {activeBrokerFlag === "grow" && (
                  <>
                    <div className="col-md-6">
                      <label className="form-label">Order Type</label>
                      <input
                        type="text"
                        className="form-control"
                        value={modifyOrderModalDetails.orderType}
                        disabled
                        placeholder="MARKET / LIMIT / SL"
                      />
                    </div>
                    <div className="col-md-6">
                      <label className="form-label">Segment</label>
                      <input
                        type="text"
                        className="form-control"
                        value={modifyOrderModalDetails.segment}
                        disabled
                      />
                    </div>
                  </>
                )}
                {isAngelOneBroker && (
                  <>
                    <div className="col-md-6">
                      <label className="form-label">Product Type</label>
                      <input
                        type="text"
                        className="form-control"
                        disabled
                        value={modifyOrderModalDetails.producttype}
                      />
                    </div>
                    <div className="col-md-6">
                      <label className="form-label">Duration</label>
                      <input
                        type="text"
                        className="form-control"
                        disabled
                        value={modifyOrderModalDetails.duration}
                      />
                    </div>
                  </>
                )}
                <div className="col-12">
                  <label className="form-label">
                    {activeBrokerFlag === "grow"
                      ? "Groww Order ID"
                      : isAngelOneBroker
                        ? "AngelOne Order ID"
                        : "Exchange Order ID"}
                  </label>
                  <input
                    type="text"
                    className="form-control"
                    disabled
                    value={
                      activeBrokerFlag === "grow"
                        ? modifyOrderModalDetails.growwOrderId
                        : isAngelOneBroker
                          ? modifyOrderModalDetails.angelOrderId
                          : modifyOrderModalDetails.exchOrderID
                    }
                  />
                </div>
              </div>
            </div>
            <div className="modal-footer modal__footer_btn" style={{ width: "100%" }}>
              <div style={{ width: "100%", display: "flex", justifyContent: "space-around" }}>
                <button
                  type="button"
                  className="btn btn-primary"
                  data-bs-dismiss="modal"
                  style={{ width: "45%" }}
                >
                  {tr("cancel")}
                </button>
                <button
                  type="button"
                  className="btn btn-success"
                  onClick={submitModifyOrder}
                  disabled={isModifySubmitting}
                  style={{ width: "45%" }}
                >
                  {isModifySubmitting ? "Modifying..." : "Modify Order"}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Cancel Order Confirmation Modal */}
      <button
        id="closeCancelOrderModalBtn"
        type="button"
        className="d-none"
        data-bs-dismiss="modal"
        data-bs-target="#CancelOrderConfirmModal"
      />
      <div
        className="modal fade"
        id="CancelOrderConfirmModal"
        aria-labelledby="CancelOrderConfirmModalLabel"
        aria-hidden="true"
      >
        <div className="modal-dialog modal-dialog-centered modal-sm">
          <div className="modal-content">
            <div
              className="modal-header"
              style={{ backgroundColor: "#fff7f7", borderBottom: "1px solid #F5B5B5" }}
            >
              <h5 className="modal-title" id="CancelOrderConfirmModalLabel">
                Confirm Cancel Order
              </h5>
              <button
                type="button"
                className="btn-close"
                data-bs-dismiss="modal"
                aria-label={tr("close")}
                onClick={() =>
                  setCancelOrderModalDetails({ show: false, order: null, symbol: "" })
                }
              />
            </div>
            <div className="modal-body">
              <p style={{ marginBottom: "10px", color: "#334155" }}>
                Are you sure you want to cancel this order?
              </p>
              <div
                style={{
                  background: "#F8FAFC",
                  border: "1px solid #E2E8F0",
                  borderRadius: "8px",
                  padding: "10px 12px",
                  fontSize: "13px",
                  color: "#475569",
                }}
              >
                <strong>Symbol:</strong> {cancelOrderModalDetails.symbol || "-"}
              </div>
            </div>
            <div className="modal-footer">
              <button
                type="button"
                className="btn btn-light"
                data-bs-dismiss="modal"
                onClick={() =>
                  setCancelOrderModalDetails({ show: false, order: null, symbol: "" })
                }
              >
                {tr("cancel")}
              </button>
              <button
                type="button"
                className="btn btn-danger"
                onClick={cancelExistingOrder}
                disabled={isCancelSubmitting}
              >
                {isCancelSubmitting ? "Cancelling..." : "Yes, Cancel Order"}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* MarketDepth Modal */}
      {/* Market Depth Popover */}
      {showMarketDepth && (
        <MarketDepthPopover
          popoverRef={marketDepthPopoverRef}
          position={marketDepthPosition}
          depthRows={currentMDModal
            ? isAngelOneBroker
              ? (Array.isArray(marketDepth) ? marketDepth : [])
              : marketDepthCache[currentMDModal.ScripCode] || []
            : []}
          onClose={closeMarketDepthPopover}
          tr={tr}
          formatNumber={formatNumber}
        />
      )}






      {/* CDSL Authorization Modal */}

      <button
        id="openCDSLModalBtn"
        type="button"
        class="d-none"
        data-bs-toggle="modal"
        data-bs-target="#CDSLAuthorizationModal"
      ></button>



      <div
        className="modal fade"
        id="CDSLAuthorizationModal"
        aria-labelledby="CDSLAuthorizationModalLabel"
        aria-hidden="true"
        data-bs-backdrop="static"
        data-bs-keyboard="false"
      >
        <div className="modal-dialog modal-dialog-centered modal-md">
          <div
            className="modal-content shadow-lg rounded-4"
            style={{
              backgroundColor: '#ffffffff',
              fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif",
              border: "2px solid #ff6b35"
            }}
          >

            <div className="modal-header pb-0" style={{ borderBottom: "2px solid #ff6b35" }}>
              <h5
                className="modal-title"
                id="CDSLAuthorizationModalLabel"
                style={{
                  color: '#212529',
                  fontWeight: '700',
                  fontSize: '1.5rem',
                  paddingBottom: "12px"
                }}
              >
                {tr("authorizeCDSLAccess")}
              </h5>
              <button
                type="button"
                className="btn-close"
                data-bs-dismiss="modal"
                aria-label={tr("close")}
                style={{ filter: 'brightness(0.5)' }}
              />
            </div>

            <div className="modal-body py-4 text-center" style={{ color: '#2e3031ff' }}>
              <div className="mb-3">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="64"
                  height="64"
                  fill="#ff6b35"
                  className="bi bi-shield-lock"
                  viewBox="0 0 16 16"
                >
                  <path d="M5.338 1.59a61.44 61.44 0 0 0-2.837.856.481.481 0 0 0-.328.39c-.554 4.157.726 7.19 2.253 9.188a10.725 10.725 0 0 0 2.287 2.233c.346.244.652.42.893.533.12.057.218.095.293.118a.55.55 0 0 0 .101.025.615.615 0 0 0 .1-.025c.076-.023.174-.061.294-.118.24-.113.547-.29.893-.533a10.726 10.726 0 0 0 2.287-2.233c1.527-1.997 2.807-5.031 2.253-9.188a.48.48 0 0 0-.328-.39c-.651-.213-1.75-.56-2.837-.855C9.552 1.29 8.531 1.067 8 1.067c-.53 0-1.552.223-2.662.524zM5.072.56C6.157.265 7.31 0 8 0s1.843.265 2.928.56c1.11.3 2.229.655 2.887.87a1.54 1.54 0 0 1 1.044 1.262c.596 4.477-.787 7.795-2.465 9.99a11.775 11.775 0 0 1-2.517 2.453 7.159 7.159 0 0 1-1.048.625c-.28.132-.581.24-.829.24s-.548-.108-.829-.24a7.158 7.158 0 0 1-1.048-.625 11.777 11.777 0 0 1-2.517-2.453C1.928 10.487.545 7.169 1.141 2.692A1.54 1.54 0 0 1 2.185 1.43 62.456 62.456 0 0 1 5.072.56z" />
                  <path d="M9.5 6.5a1.5 1.5 0 0 1-1 1.415l.385 1.99a.5.5 0 0 1-.491.595h-.788a.5.5 0 0 1-.49-.595l.384-1.99a1.5 1.5 0 1 1 2-1.415z" />
                </svg>
              </div>

              <h3 style={{ fontWeight: '600', marginBottom: '1rem', color: '#212529' }}>
                {tr("cdslSellAuthorizationRequired")}
              </h3>

              <p style={{ fontSize: '1.05rem', marginBottom: '2rem', lineHeight: '1.6' }}>
                {tr("cdslAuthorizationDescription")}
              </p>

              <div className="d-flex justify-content-center gap-3">
                <button
                  type="button"
                  className="btn btn-outline-secondary px-4 py-2 fw-semibold"
                  data-bs-dismiss="modal"
                  style={{
                    borderRadius: '30px',
                    fontSize: '1rem',
                    letterSpacing: '0.03em'
                  }}
                >
                  {tr("cancel")}
                </button>

                <form
                  name="CDSLAuthForm"
                  method="post"
                  action="https://dev-openapi.5paisa.com/WebVendorLogin/EDISAuthorization/Authorization"
                  target="_blank"
                  style={{ margin: 0 }}
                >
                  <input
                    type="hidden"
                    name="AccessToken"
                    value={sharedData.accessToken}
                  />
                  <input
                    type="hidden"
                    name="ClientCode"
                    value={sharedData.ClientCode}
                  />
                  <input
                    type="hidden"
                    name="VendorKey"
                    value={sharedData.api_key}
                  />
                  <input
                    type="hidden"
                    name="ResponseURL"
                    value={`https://beta.infomanav.in/zerodhaReact/authorization/${sharedData.ID}`}
                  />

                  <button
                    type="submit"
                    className="btn px-4 py-2 fw-semibold"
                    style={{
                      borderRadius: '30px',
                      fontSize: '1rem',
                      letterSpacing: '0.03em',
                      backgroundColor: '#ff6b35',
                      color: 'white',
                      border: 'none'
                    }}
                    data-bs-dismiss="modal"
                  >
                    {tr("authorize")}
                  </button>
                </form>
              </div>
            </div>

          </div>
        </div>
      </div>






    </>
  );
}

export default Orders;
